// A static type checker with light type inference.
//
// Beyond surfacing mistakes as diagnostics (the playground's red squiggles), the
// checker records a *semantic index* as it walks: the inferred type of every
// expression, every identifier reference resolved to its definition, the lexical
// scopes and their bindings, and the program's top-level symbols. That index is
// what powers the editor's hovers, go-to-definition, and autocompletion.
//
// Inference is local and best-effort: annotations are required on parameters and
// fields, and `let` / `for` / `match` bindings take the type of their
// initializer. Anything it cannot resolve becomes `unknown`, which is compatible
// with everything, so unknowns never cascade into noise.

import * as ast from "./ast.js";
import { Span } from "./token.js";
import { BOOL, CHAR, FLOAT, INT, STRING, Type, UNKNOWN, VOID } from "./types.js";
import { builtinContainerInherentImpls, builtinEnums, builtinFns, builtinImpls, builtinStructs, builtinTraits, BUILTINS, containerTypeParams, preludeFns } from "./stdlib.js";
import { Elaboration, emptyElaboration, IterResolution } from "../middle-end/elaboration.js";

export interface Diagnostic {
  severity: "error" | "warning";
  message: string;
  span: Span;
}

/** Overloadable binary operators and the built-in trait/method each desugars to.
 *  `a + b` -> `a.add(b)` when `a`'s type implements `Add`. Shared by the checker
 *  and, mirrored, by the interpreter. */
export const OPERATOR_TRAITS: Record<string, { trait: string; method: string }> = {
  "+": { trait: "Add", method: "add" },
  "-": { trait: "Sub", method: "sub" },
  "*": { trait: "Mul", method: "mul" },
  "/": { trait: "Div", method: "div" },
};

// ---------------------------------------------------------------------------
// Semantic index — everything the editor's language features consume.
// ---------------------------------------------------------------------------

export type DefKind =
  | "struct"
  | "enum"
  | "trait"
  | "variant"
  | "fn"
  | "method"
  | "field"
  | "param"
  | "let"
  | "loop-var"
  | "binding"
  | "builtin";

/** A named thing the user can hover, complete, or jump to. */
export interface DefInfo {
  name: string;
  kind: DefKind;
  /** Span of the defining identifier — the go-to-definition target. */
  nameSpan: Span;
  type: Type;
  /** One-line signature shown in hovers and completion detail. */
  detail: string;
  /** Enclosing symbol, e.g. the struct a field belongs to. */
  container?: string;
  /** `true` for a struct field carrying the `key` modifier (a Table's primary key). */
  key?: boolean;
}

/** A resolved use of an identifier / field name. */
export interface RefInfo {
  span: Span;
  def: DefInfo | null;
  type: Type;
}

/** A stable identifier for a definition: the file and offset where its name is
 *  written. Every use of the same symbol resolves to a def with this same key, so
 *  it is what ties the two ends of a reference edge together — independent of
 *  whether the `DefInfo` objects are shared by identity. */
export type DefId = string;

export function defId(nameSpan: Span): DefId {
  return `${nameSpan.source ?? ""}:${nameSpan.start.offset}`;
}

/** The program's definitions and uses as a graph. Every definition is a node
 *  (`defs`, keyed by `DefId`); every use is an edge to the definition it resolves
 *  to, and the edges are stored grouped by their target (`uses`, a definition's
 *  incoming edges). The two navigation features are the two ends of this edge set:
 *  go-to-definition walks from a use to its definition, and find-usages walks the
 *  same edges in reverse, from a definition to its uses. Built from the recorded
 *  refs and the collected definitions (see `buildReferenceGraph`). */
export interface ReferenceGraph {
  defs: Map<DefId, DefInfo>;
  uses: Map<DefId, Span[]>;
}

/** The inferred type of an expression, keyed by its source span (for hovers). */
export interface NodeType {
  span: Span;
  type: Type;
}

/** A lexical scope and the bindings visible within it. */
export interface ScopeInfo {
  span: Span;
  defs: DefInfo[];
  byName: Map<string, DefInfo>;
}

export interface SemanticIndex {
  diagnostics: Diagnostic[];
  refs: RefInfo[];
  nodeTypes: NodeType[];
  scopes: ScopeInfo[];
  /** Top-level symbols: structs, enums, functions, and variant constructors. */
  globals: DefInfo[];
  structFields: Map<string, DefInfo[]>;
  enumVariants: Map<string, DefInfo[]>;
  /** Callable members contributed by `impl`s (and, keyed by trait name, a trait's
   *  method signatures) — the methods `x.method(...)` resolves against, and what
   *  member completion offers after a `.`. */
  typeMethods: Map<string, Member[]>;
  /** A generic struct/enum/trait's declared type-parameter names, keyed by type
   *  name — so the language service can specialize its members to an instance's
   *  arguments (container params come from `containerTypeParams`). */
  typeParams: Map<string, string[]>;
  /** Each callable's *net inferred* effect set (after discharge), keyed by the
   *  effect-system callable key (`fn:<name>`, `method:<owner>.<name>`) and in the
   *  canonical order `io retry raises spawn`. This is the same information the effect
   *  constraints are checked against; it is exposed so the language service can
   *  render a function's inferred effects as an inlay hint after its return type.
   *  A callable with no effects is absent (an empty set = pure). */
  effects: Map<string, ast.EffectName[]>;
  /** The checker's per-node decisions (expression types, call targets, operator
   *  overloads, `for`-loop iteration plans), keyed by node identity — for backends
   *  to lower instead of re-deriving. See `middle-end/elaboration.ts`. */
  elaboration: Elaboration;
  /** Definitions and their uses, linked as a graph — the shared basis for
   *  go-to-definition and find-usages. Derived from `refs` (which additionally
   *  carry each use's type, for hovers) plus every collected definition. */
  referenceGraph: ReferenceGraph;
}

// ---------------------------------------------------------------------------
// Members — the one notion of "what can you access on a value of type T".
//
// Field access (`obj.field`) and method calls (`obj.method(args)`) both resolve
// through `typeMembers`, so the checker walks a member chain uniformly instead
// of special-casing each type at each node. A type declaration effectively
// *expands* into a member set: a struct into its fields, a container into its
// methods. The only per-type knowledge lives in the generators below.
// ---------------------------------------------------------------------------

/** A member of a type. Extends `DefInfo` so it doubles as the hover / go-to-def
 *  target. A plain field has no `params`; a method carries its parameter types
 *  and a `ret` that computes the result from the *actual* arguments — which is
 *  what lets `group_by` return `Dict<K, Table<S>>` for the `K` it is handed. */
export interface Member extends DefInfo {
  params?: { label: string; type: Type; role?: string; default?: ast.Expr }[];
  ret?: (argTypes: Type[]) => Type;
  /** A generic method's own type parameters (`U` in `map<U>(...)`), if any. When
   *  present, the parameter/return `Type`s carry those parameters as type
   *  variables, and a call infers them from the arguments (see
   *  `checkGenericMemberCall`). */
  typeParams?: ast.TypeParam[];
  /** The trait an impl method satisfies (shown as `[Trait]` in its detail). Kept so
   *  `members` can rebuild the detail after substituting the instance's arguments. */
  viaTrait?: string;
  /** Declared `builtin` — implemented natively by each backend rather than by an
   *  STM body. Read when an operator resolves through a trait, to tell a `builtin`
   *  impl (which lowers to the backend's own primitive, e.g. the scalars' `Eq`)
   *  from a user-written one (which lowers to a call). */
  builtin?: boolean;
}

/** A method's one-line signature (`fn set(self, key: string, value: int)`). Shared
 *  by `methodMember` (declared form) and `members` (after substituting a generic
 *  instance's arguments, so a `Dict<string, int>` completes `set` with concrete
 *  types, not `K`/`V`). */
function methodDetail(
  name: string,
  typeParams: ast.TypeParam[] | undefined,
  params: { label: string; type: Type }[],
  ret: Type,
  viaTrait?: string
): string {
  const tps = typeParams?.length ? `<${typeParams.map((p) => p.name).join(", ")}>` : "";
  const paramList = ["self", ...params.map((p) => `${p.label}: ${typeStr(p.type)}`)].join(", ");
  const retStr = ret.k === "void" ? "" : ` ${typeStr(ret)}`;
  const via = viaTrait ? `  [${viaTrait}]` : "";
  return `fn ${name}${tps}(${paramList})${retStr}${via}`;
}

/** Substitute the type variables in `subst`, leaving any variable absent from the
 *  map in place (so a method's own type parameters survive for the call site to
 *  infer). A pure companion to the checker's `subst`. */
function substType(t: Type, subst: Map<string, Type>): Type {
  if (t.k === "var") return subst.get(t.name) ?? t;
  if (t.k === "fn") return { k: "fn", params: t.params.map((p) => substType(p, subst)), ret: substType(t.ret, subst) };
  if (t.k === "generic" || t.k === "enum-generic" || t.k === "struct-generic" || t.k === "trait")
    return { ...t, args: t.args.map((a) => substType(a, subst)) };
  return t;
}

/** Specialize a generic type's members by binding its declared parameters
 *  (`paramNames`) to the instance's `args`: each method's parameter/return `Type`
 *  and its `detail` signature are rewritten, so a `Dict<string, int>`'s `set`
 *  reads `set(self, key: string, value: int)`. A method's own type parameters
 *  (`U` in `map<U>`) aren't in `subst`, so they stay for the call site to infer. */
function substituteMembers(base: Member[], paramNames: string[], args: Type[]): Member[] {
  const subst = new Map<string, Type>();
  paramNames.forEach((name, i) => subst.set(name, args[i] ?? UNKNOWN));
  return base.map((m) => {
    const t = substType(m.type, subst);
    const params = m.params?.map((p) => ({ ...p, type: substType(p.type, subst) }));
    const ret = m.ret ? (a: Type[]) => substType(m.ret!(a), subst) : undefined;
    const detail = m.params ? methodDetail(m.name, m.typeParams, params!, t, m.viaTrait) : m.detail;
    return { ...m, type: t, params, ret, detail };
  });
}

/** The members of `type`. `fieldsOf` supplies a struct's declared fields and
 *  `methodsOf` its `impl`/trait methods (the checker and the language service each
 *  pass their own index). `typeParamsOf`, when given, returns a generic type's
 *  declared parameter names so the members are specialized to the instance's
 *  arguments (concrete types in hovers/completions, and for type-checking). This is
 *  the single source both field access and method-call checking — and editor
 *  completion — read. */
export function typeMembers(
  type: Type,
  fieldsOf: (struct: string) => DefInfo[],
  methodsOf: (typeName: string) => Member[] = () => [],
  typeParamsOf: (type: Type) => string[] | undefined = () => undefined
): Member[] {
  const base = typeMembersRaw(type, fieldsOf, methodsOf);
  const paramNames = typeParamsOf(type);
  if (!paramNames || !("args" in type)) return base;
  return substituteMembers(base, paramNames, (type as Extract<Type, { args: Type[] }>).args);
}

function typeMembersRaw(
  type: Type,
  fieldsOf: (struct: string) => DefInfo[],
  methodsOf: (typeName: string) => Member[]
): Member[] {
  switch (type.k) {
    case "struct":
    case "struct-generic":
      // A struct's own fields, plus any methods `impl`'d for it (trait methods).
      // For a generic instantiation the field types still carry the struct's type
      // variables; the checker's `members` substitutes the concrete arguments.
      return [...fieldsOf(type.name).map((f) => ({ ...f })), ...methodsOf(type.name)];
    case "enum":
    case "enum-generic":
      // Enums have no fields to access directly, but they can carry `impl` methods.
      // A generic instantiation's method types are substituted by `members`.
      return methodsOf(type.name);
    case "trait":
      // A trait value's members are the trait's declared method signatures.
      return methodsOf(type.name);
    case "var":
      // A type parameter's members are the union of its bound traits' methods.
      return type.bounds.flatMap((b) => methodsOf(b));
    case "string":
      // The primitive string's methods come from the `impl str { … }` block in
      // the prelude — an *inherent* impl on the `str` alias (see `strings.stm`).
      return methodsOf("str");
    case "char":
      // Likewise the `impl char { … }` block. `char` has no field access, only
      // these methods (`code`, `isDigit`, …).
      return methodsOf("char");
    case "int":
    case "float":
    case "bool":
      // The remaining scalars carry no inherent impl, but the prelude does give
      // them trait impls (`Ord`, `Add`, `Eq` — see core.stm), keyed by the type's
      // own name. Surfacing them here is what lets an operator resolve through the
      // trait for a scalar the same way it does for a user type.
      return methodsOf(type.k);
    case "generic":
      // A container's methods are ordinary `impl` methods (its inherent impl, plus
      // any user `impl<T> Trait for Set<T>`), resolved by head name like a struct's.
      return methodsOf(type.name);
    default:
      return [];
  }
}

/** Resolve the type arguments of a container reference, padded to its declared
 *  arity (`Dict` → 2). Like a generic struct, a container is not inferred — its
 *  arguments are named at construction. */
function containerArgs(name: string, resolve: (i: number) => Type | undefined): Type[] {
  return containerTypeParams(name).map((_, i) => resolve(i) ?? UNKNOWN);
}

const ZERO_POS = { offset: 0, line: 1, col: 1 };
const ZERO_SPAN: Span = { start: ZERO_POS, end: ZERO_POS };

/** The built-in `Error` trait as a type: what `catch e { … }` binds, and the bound
 *  `raise` requires of the value it throws (declared in `stdlib/core.stm`). */
const ERROR_TRAIT: Type = { k: "trait", name: "Error", args: [] };

/** Deep-clone an AST node (or array of nodes). Used to fill an omitted defaulted
 *  argument with a *fresh* copy of the default expression at each call site, so it
 *  is checked and lowered independently — a `List` default becomes a distinct list
 *  every call rather than a shared one. Spans and plain values are copied by
 *  reference/value; only the object/array structure is duplicated. */
function cloneNode<T>(n: T): T {
  if (Array.isArray(n)) return (n as unknown[]).map(cloneNode) as unknown as T;
  if (n !== null && typeof n === "object") {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(n as object)) out[k] = cloneNode((n as Record<string, unknown>)[k]);
    return out as T;
  }
  return n;
}

/** Parse + type-check, returning diagnostics only (CLI / tests). */
export function check(program: ast.Program): Diagnostic[] {
  return analyze(program).diagnostics;
}

/** Full semantic analysis: diagnostics plus the editor's index. */
export function analyze(program: ast.Program): SemanticIndex {
  return new Checker(program).run();
}

class Env {
  constructor(
    readonly scope: ScopeInfo,
    private readonly parent: Env | null = null
  ) {}
  lookup(name: string): DefInfo | undefined {
    return this.scope.byName.get(name) ?? this.parent?.lookup(name);
  }
  get(name: string): Type | undefined {
    return this.lookup(name)?.type;
  }
  declare(def: DefInfo): void {
    this.scope.byName.set(def.name, def);
    this.scope.defs.push(def);
  }
}

class Checker {
  private diags: Diagnostic[] = [];
  private refs: RefInfo[] = [];
  private nodeTypes: NodeType[] = [];
  /** Each callable's net inferred effect set, filled by `checkEffects` from the
   *  four closures it computes (see `SemanticIndex.effects`). */
  private effectsByCallable = new Map<string, ast.EffectName[]>();
  /** Per-node decisions accumulated during the walk (see elaboration.ts). */
  private elab: Elaboration = emptyElaboration();
  /** A stack of return-type sinks for the block-bodied lambdas currently being
   *  checked (innermost last). A lambda's result type is *inferred* by collecting
   *  its `return` types into `types`; `expected`, when the lambda is checked against
   *  a known function type, is the expected type each returned expression is checked
   *  against (local bidirectional inference — an `() -> { return None() }` against
   *  `() -> Option<int>` resolves the `None()`). */
  private returnSinks: { types: Type[]; expected?: Type }[] = [];
  /** Depth of the argument re-check pass (see `checkGenericCall`). While positive,
   *  `error` and `noteCall` are suppressed: the re-check only refines already-
   *  recorded types and must not re-report diagnostics or re-count effect edges. */
  private recheckDepth = 0;
  private scopes: ScopeInfo[] = [];
  private globals: DefInfo[] = [];
  private structFields = new Map<string, DefInfo[]>();
  private enumVariants = new Map<string, DefInfo[]>();

  private structs = new Map<string, ast.StructDecl>();
  /** Names of structs that are value types (every field `key`; see `isValueType`). */
  private valueTypes = new Set<string>();
  /** Enums usable by value: every variant field is itself value-compatible, so
   *  the enum has a finite inline, hashable representation (see `computeValueTypeEnums`). */
  private valueTypeEnums = new Set<string>();
  private enums = new Map<string, ast.EnumDecl>();
  private variantOf = new Map<string, string>(); // variant name -> enum name
  private variantDecl = new Map<string, ast.EnumVariant>();
  private fns = new Map<string, ast.FnDecl>();
  private globalDefs = new Map<string, DefInfo>(); // name -> callable/type def

  private traits = new Map<string, ast.TraitDecl>();
  private impls: ast.ImplDecl[] = [];

  // -- effect system (IO inference) ---------------------------------------
  // Functions carry a set drawn from a closed vocabulary — `io`, `retry`, `raises`,
  // `spawn`. A builtin *declares* its effects with a trailing `! …` annotation (the
  // checker can't see a native body); every other function's set is *inferred* — a
  // function is IO iff it (transitively) calls an IO function, spawning iff it
  // (transitively) reaches a `go`, and so on. IO and `spawn` are then forbidden
  // inside a `txn`, whose body may abort and re-run.
  //
  // Callables are keyed by a string: a free function / builtin is `fn:<name>`; a
  // method is `method:<owner>.<name>`, where `owner` is the impl target type (for
  // an explicit impl method) or the trait (for a trait default body). The call
  // graph is gathered during the walk; the IO set is a fixpoint over it (see
  // `checkEffects`), computed once the whole program has been walked.

  /** The callable whose body is currently being walked (its key), or `null` at
   *  top level. Calls encountered are recorded as edges out of it. */
  private currentCallable: string | null = null;
  /** Callable key -> the keys of the callables it calls. */
  private calleesOf = new Map<string, Set<string>>();
  /** Callable keys seeded IO up front: `io`-marked builtins, functions, methods. */
  private ioSeeds = new Set<string>();
  /** Callable key -> indices of its parameters it *invokes* (calls as a function).
   *  A higher-order function like `apply(f, x) { f(x) }` invokes parameter 0, so
   *  passing an IO function there makes the *call* IO — a lightweight slice of
   *  effect polymorphism (see `collectInvokedParams`). Computed syntactically up
   *  front, since a call site may precede the callee's declaration. */
  private invokedParams = new Map<string, Set<number>>();
  /** Obligations lexically inside a `txn`: each is a callable the transaction
   *  reaches, validated afterward against the two effect sets banned in a
   *  transaction — `io` and `spawn` — and reported at `span`. */
  private txnObligations: { key: string; name: string; span: Span }[] = [];
  /** The *retrying* effect — the dual of `io`, and (like `io`) a fixpoint over the
   *  call graph. A callable is retrying if it has a **bare** `retry` (one not
   *  inside a `txn` block in its body) *or* it calls a retrying callable outside a
   *  `txn` — the `retry` needs an enclosing transaction, so it forwards up until a
   *  `txn` discharges it. `retrySeeds` holds the bases (bare-`retry` callables plus
   *  the seeded `orElse`); `retryEdges` carries the propagation; the closure is
   *  computed in `checkEffects`. (A `retry` *within* a `txn`, or a call made inside
   *  one, is consumed by that transaction and does not forward.) */
  private retrySeeds = new Set<string>();
  /** First bare-`retry` span per retrying callable, for error reporting. */
  private retrySeedSpans = new Map<string, Span>();
  /** Retry-propagation edges: caller -> callees called *outside* a `txn` (and not
   *  `go`-spawned). Retry forwards along these — an in-`txn` call is discharged and
   *  excluded, so it never forwards. The masked dual of `calleesOf` for `retry`. */
  private retryEdges = new Map<string, Set<string>>();
  /** Calls in an *un-catchable* position — `main`'s top-level (non-`txn`) calls and
   *  every `go`-spawned call — that have no enclosing transaction to catch a
   *  `retry`. Each is validated against the retry closure afterward: if the callee
   *  (transitively) retries, there is nothing to catch it, so it is an error. */
  private retryRootObligations: { key: string; name: string; span: Span }[] = [];
  /** The *raising* effect — the third of the closed set, and (like `io` and
   *  `retry`) a fixpoint over the call graph. A callable raises if it has an
   *  **uncaught** `raise` in its body *or* calls a raising callable outside a `try`.
   *  `catch` is the discharge point, and since it takes no type it discharges
   *  unconditionally: nothing propagates past a `try`/`catch` unless the handler
   *  itself raises (which a `raise e` re-raise in the handler body does — that body is
   *  walked *outside* the handler stack, so it re-seeds the effect). */
  private raisesSeeds = new Set<string>();
  /** Raise-propagation edges: caller -> callees called *outside* any `try`
   *  (and not `go`-spawned). The masked dual of `calleesOf` for `raises`. */
  private raisesEdges = new Map<string, Set<string>>();
  /** Calls lexically inside a `txn` whose raise nothing in the transaction catches.
   *  Each is validated against the raises closure afterward: an exception unwinding
   *  out of a `txn` would discard it mid-flight (and may have been computed from a
   *  stale snapshot), so v1 forbids it — catch it inside the transaction instead.
   *  The `raises` analogue of `txnObligations`. */
  private txnRaiseObligations: { key: string; name: string; span: Span }[] = [];
  /** The *spawning* effect — the fourth of the closed set, seeded by a `go`
   *  statement and (like `io`) an unmasked fixpoint over the call graph: nothing
   *  discharges `spawn`, so a callable spawns if it has a `go` in its body or calls a
   *  spawning callable anywhere. Its constraint is `io`'s: a spawning callable may
   *  not be called inside a `txn`, because an optimistic transaction may abort and
   *  re-run, which would spawn the thread again (the runtime's "cannot spawn a thread
   *  (`go`) inside a transaction", lifted to a compile-time error). */
  private spawnSeeds = new Set<string>();
  /** The `try` bodies lexically enclosing the point being walked, innermost last —
   *  the `raises` effect's discharge stack. A `catch` takes no type, so any enclosing
   *  handler discharges; all that has to be recorded is `txnDepth`, how deep in `txn`s
   *  the `try` itself sits (a handler *outside* the transaction cannot make a raise
   *  inside it safe). Saved and cleared per callable, like `currentCallable`. */
  private handlers: { txnDepth: number }[] = [];
  /** True while walking the call `go`-spawned in a `go` statement: its calls root a
   *  fresh thread (no enclosing `txn`), so they become root obligations rather than
   *  forwarding `retry` to the spawning callable. */
  private spawningGo = false;
  /** How deep in `txn` bodies the walk currently is (0 = outside any transaction). */
  private txnDepth = 0;
  /** `<typeName>.<method>` for which an explicit impl method body exists. */
  private explicitMethods = new Set<string>();
  /** `<typeName>.<method>` served by an inherited trait default -> that trait's name. */
  private inheritedDefaults = new Map<string, string>();
  /** `<traitName>.<method>` that carries a default body. */
  private traitDefaults = new Set<string>();
  /** trait name -> the impls of that trait (for expanding a dynamic method call). */
  private implsOfTrait = new Map<string, ast.ImplDecl[]>();
  /** Callable members per type name (impls) and per trait name (signatures). */
  private typeMethods = new Map<string, Member[]>();
  /** type name -> set of trait names it implements. A generic container is keyed
   *  by its head name (`Set`). */
  private implsByType = new Map<string, Set<string>>();
  /** type name -> trait name -> the trait's type arguments for that impl, e.g.
   *  `Range`'s `Iterator` -> `[int]`. Drives `for x in it` element typing. */
  private implTraitArgs = new Map<string, Map<string, Type[]>>();
  /** Type parameters in scope while checking the current generic `impl` body, e.g.
   *  `T` in `impl<T> … for Set<T>`. They resolve to `unknown` (best-effort). */
  private currentTypeParams = new Set<string>();
  /** Generic-function type parameters in scope (name -> its `var` type), so a
   *  param/return/`let` annotation `T` resolves to `{ k: "var", … }` rather than
   *  an unknown type. Set by `withTypeVars` around a generic fn's declaration,
   *  body, and call sites. */
  private typeVars = new Map<string, Type>();
  /** What `Self` resolves to right now: the target type while checking an `impl`,
   *  or `unknown` while checking a `trait` (abstract). `null` elsewhere, where
   *  `Self` is an error. */
  private currentSelf: Type | null = null;
  private implementsTrait = (typeName: string, traitName: string): boolean =>
    this.implsByType.get(typeName)?.has(traitName) ?? false;
  /** The name a type's `impl`s are keyed under (for a trait-bound / method lookup),
   *  or null for a type that can carry none. A user type is its own name; a
   *  primitive maps to the name its prelude `impl`s use — `string` to `str` (the
   *  alias the inherent `impl str` and `impl Ord for str` are written under). */
  private implName(t: Type): string | null {
    if (t.k === "struct" || t.k === "enum" || t.k === "generic") return t.name;
    if (t.k === "int" || t.k === "float" || t.k === "bool" || t.k === "char") return t.k;
    if (t.k === "string") return "str";
    return null;
  }
  /** Whether a concrete (non-variable) type satisfies a type parameter's trait
   *  bound: it implements the trait. Nothing is special-cased here — the built-in
   *  ordered scalars satisfy `Ord` because the prelude *declares* `impl Ord for int`
   *  (etc.), so `fn min<T: Ord>` takes an `int` through the same path a user `Ord`
   *  type takes. (A scalar comparison still lowers natively; the impl's `cmp` is
   *  what a *generic* body reaches when it can't assume a scalar.) */
  private satisfiesBound(concrete: Type, trait: string): boolean {
    const name = this.implName(concrete);
    return name !== null && this.implementsTrait(name, trait);
  }
  private implementsTraitType = (value: Type, trait: Extract<Type, { k: "trait" }>): boolean => {
    if (
      value.k !== "struct" &&
      value.k !== "struct-generic" &&
      value.k !== "enum" &&
      value.k !== "enum-generic" &&
      value.k !== "generic"
    )
      return false;
    const actual = this.traitArgsFor(value, trait.name);
    return (
      actual !== null &&
      actual.length === trait.args.length &&
      actual.every((arg, i) => this.sameType(arg, trait.args[i] ?? UNKNOWN))
    );
  };

  /** User declarations plus the built-in prelude (e.g. the `Option` enum), which
   *  a program may shadow by declaring the same name. */
  private decls: ast.Decl[];

  /** Built-in `fn` declarations by name — used to type a *generic* builtin call
   *  (`race<T>`) through the generic-inference path, since `BUILTINS` only carries a
   *  flat (non-generic) return type. */
  private builtinDecls = new Map(builtinFns().map((f) => [f.name, f] as const));

  constructor(private program: ast.Program) {
    const prelude = [...builtinStructs(), ...builtinEnums(), ...builtinTraits(), ...preludeFns()].filter(
      (b) => !ast.shadowsPrelude(program, b.name)
    );
    // Built-in impls (the iterator adapters behind `Iterable.map`, the `Range`
    // iterable) are additive — but an impl attached to a built-in type a program
    // shadows must go with it: if the program declares its own `struct Range`, the
    // stdlib `impl Iterable for Range` would otherwise dangle onto it. So drop any
    // built-in impl whose target type the program redeclares.
    const shadowedTypes = new Set(
      program.decls.filter((d) => d.kind === "struct" || d.kind === "enum").map((d) => d.name)
    );
    // The built-in containers' *inherent* impls (`impl<K, V> Dict<K, V> { … }`)
    // join the prelude here, so a container's flat methods (`get`/`add`/…) are
    // processed like any user impl — resolved through the same impl + substitution
    // path a generic struct's methods use, not a bespoke container model. (Their
    // `Iterable` impls are already in `builtinImpls`.)
    const impls = [...builtinImpls(), ...builtinContainerInherentImpls()].filter(
      (im) => !shadowedTypes.has(im.typeName)
    );
    this.decls = [...prelude, ...impls, ...program.decls];
    for (const d of this.decls) {
      if (d.kind === "struct") {
        // A `builtin struct` (a container like `Dict`) is NOT registered as a
        // constructible struct: its values and construction flow through the
        // container machinery (`{k:"generic"}`), and `this.structs.has(name)` is
        // consulted before the container branch at construction — so keeping it out
        // leaves that dispatch unchanged. It still gets a global type def (for
        // go-to-definition / hover) in `collectGlobals`.
        if (d.builtin) continue;
        this.structs.set(d.name, d);
        if (ast.isValueType(d)) this.valueTypes.add(d.name); // never true for a generic struct
      } else if (d.kind === "enum") {
        this.enums.set(d.name, d);
        for (const v of d.variants) {
          if (!this.variantOf.has(v.name)) {
            this.variantOf.set(v.name, d.name);
            this.variantDecl.set(v.name, v);
          }
        }
      } else if (d.kind === "trait") {
        this.traits.set(d.name, d);
      } else if (d.kind === "impl") {
        this.impls.push(d);
        let set = this.implsByType.get(d.typeName);
        if (!set) this.implsByType.set(d.typeName, (set = new Set()));
        set.add(d.trait);
      } else this.fns.set(d.name, d);
    }
    this.computeValueTypeEnums();
    this.buildEffectTables();
  }

  /** Index the impl/trait method structure so a call can be mapped to the
   *  callable(s) it may reach (see the effect-system fields above). Seed the effect
   *  sets from every declaration's trailing `! io retry` annotation. */
  private buildEffectTables(): void {
    // Built-in functions *declare* their effects (the checker can't see a native
    // body); a bodied user function may also declare them as a checked upper bound.
    // Both feed the same seeds — `io` into `ioSeeds`, `retry` into `retrySeeds` —
    // which is how `orElse`'s `! retry` seeds it (no longer a hardcoded checker seed).
    for (const [name, b] of Object.entries(BUILTINS)) {
      const key = `fn:${name}`;
      this.checkEffectSet(key, b, b.nameSpan);
      if (b.io) this.ioSeeds.add(key);
      if (b.retry) this.retrySeeds.add(key);
      if (b.raises) this.raisesSeeds.add(key);
      if (b.spawn) this.spawnSeeds.add(key);
    }
    for (const fn of this.fns.values()) this.seedDeclaredEffects(`fn:${fn.name}`, fn.effects, fn.nameSpan);
    for (const t of this.traits.values()) {
      for (const m of t.methods) {
        if (m.body) this.traitDefaults.add(`${t.name}.${m.name}`);
        this.seedDeclaredEffects(`method:${t.name}.${m.name}`, m.effects, m.nameSpan);
      }
    }
    for (const d of this.impls) {
      let list = this.implsOfTrait.get(d.trait);
      if (!list) this.implsOfTrait.set(d.trait, (list = []));
      list.push(d);
      const explicit = new Set(d.methods.map((m) => m.name));
      for (const m of d.methods) {
        this.explicitMethods.add(`${d.typeName}.${m.name}`);
        this.seedDeclaredEffects(`method:${d.typeName}.${m.name}`, m.effects, m.nameSpan);
      }
      const trait = this.traits.get(d.trait);
      if (trait) {
        for (const tm of trait.methods) {
          if (tm.body && !explicit.has(tm.name)) this.inheritedDefaults.set(`${d.typeName}.${tm.name}`, d.trait);
        }
      }
    }
    this.collectInvokedParams();
  }

  /** Seed a callable's declared effects (`! io retry raises spawn`) into the
   *  per-effect sets, after checking the set is legal. A declaration is an upper bound
   *  the checker honors: `io` marks the callable IO, `retry` marks it retrying (the
   *  seed a bare `retry` would otherwise supply — this is how a bodyless `builtin fn …
   *  ! retry` like `orElse` becomes retrying), `raises` marks it raising (the seed an
   *  uncaught `raise` would otherwise supply), `spawn` marks it spawning (the seed a
   *  `go` statement would otherwise supply). */
  private seedDeclaredEffects(key: string, effects: readonly ast.EffectName[] | undefined, span: Span): void {
    if (!effects || effects.length === 0) return;
    const set = {
      io: effects.includes("io"),
      retry: effects.includes("retry"),
      raises: effects.includes("raises"),
      spawn: effects.includes("spawn"),
    };
    this.checkEffectSet(key, set, span);
    if (set.io) this.ioSeeds.add(key);
    if (set.retry) this.retrySeeds.add(key);
    if (set.raises) this.raisesSeeds.add(key);
    if (set.spawn) this.spawnSeeds.add(key);
  }

  /** The declared effect set is not a flat bag: `retry` may run *only* inside a
   *  `txn`, while `io`, an uncaught `raise`, and a `go` spawn are all banned *inside*
   *  one — so a callable declared `! io retry`, `! retry raises` or `! retry spawn`
   *  can never be called anywhere. (`io`, `raises` and `spawn` are mutually
   *  orthogonal.) Reported at the declaration. */
  private checkEffectSet(
    key: string,
    set: { io: boolean; retry: boolean; raises: boolean; spawn: boolean },
    span: Span
  ): void {
    if (!set.retry) return;
    const why: Partial<Record<ast.EffectName, string>> = {
      io: "`io` cannot run inside a `txn`",
      raises: "a `raise` may not escape a `txn`",
      spawn: "a `go` may not spawn inside a `txn`",
    };
    const other = set.io ? "io" : set.raises ? "raises" : set.spawn ? "spawn" : null;
    if (other === null) return;
    this.error(
      `\`${other}\` and \`retry\` are incompatible effects on \`${renderCallable(key)}\`: ${why[other]}, but \`retry\` requires one`,
      span
    );
  }

  /** For every function/method body, record which of its parameters it *invokes*
   *  (calls as `param(...)`). Purely syntactic — it looks for a call whose callee
   *  names a parameter — so it needs no type resolution and can run before the
   *  main walk. A parameter shadowed by a same-named local only adds a spurious
   *  edge (conservative), which is harmless. */
  private collectInvokedParams(): void {
    const scan = (params: ast.Param[], body: ast.Block, key: string): void => {
      const index = new Map(params.map((p, i) => [p.name, i]));
      const invoked = new Set<number>();
      const visitExpr = (e: ast.Expr): void => {
        switch (e.kind) {
          case "call":
            if (e.callee.kind === "ident" && index.has(e.callee.name)) invoked.add(index.get(e.callee.name)!);
            visitExpr(e.callee);
            for (const a of e.args) visitExpr(a.value);
            return;
          case "binary":
            visitExpr(e.left);
            visitExpr(e.right);
            return;
          case "unary":
            return visitExpr(e.operand);
          case "field":
            return visitExpr(e.object);
          case "lambda":
            return e.body.kind === "block" ? visitBlock(e.body) : visitExpr(e.body);
          case "ifExpr":
            visitExpr(e.cond);
            visitExpr(e.then);
            visitExpr(e.else);
            return;
          case "list":
            return e.elements.forEach(visitExpr);
          case "brace":
            return e.entries.forEach((en) => {
              if (en.key) visitExpr(en.key);
              visitExpr(en.value);
            });
          default:
            return; // literals, bare idents
        }
      };
      const visitStmt = (s: ast.Stmt): void => {
        switch (s.kind) {
          case "block":
            return visitBlock(s);
          case "let":
            return visitExpr(s.value);
          case "assign":
            visitExpr(s.target);
            visitExpr(s.value);
            return;
          case "exprStmt":
            return visitExpr(s.expr);
          case "return":
            if (s.value) visitExpr(s.value);
            return;
          case "if":
            visitExpr(s.cond);
            visitBlock(s.then);
            if (s.else) (s.else.kind === "if" ? visitStmt(s.else) : visitBlock(s.else));
            return;
          case "while":
            visitExpr(s.cond);
            return visitBlock(s.body);
          case "for":
            visitExpr(s.iterable);
            return visitBlock(s.body);
          case "go":
            return visitExpr(s.call);
          case "txn":
            return visitBlock(s.body);
          case "raise":
            visitExpr(s.value);
            if (s.cause) visitExpr(s.cause);
            return;
          case "try":
            visitBlock(s.body);
            visitBlock(s.handler.body);
            return;
          case "match":
            visitExpr(s.subject);
            for (const arm of s.arms) visitBlock(arm.body);
            return;
        }
      };
      const visitBlock = (b: ast.Block): void => {
        for (const st of b.stmts) visitStmt(st);
      };
      visitBlock(body);
      if (invoked.size) this.invokedParams.set(key, invoked);
    };
    for (const [name, fn] of this.fns) if (!fn.builtin) scan(fn.params, fn.body, `fn:${name}`);
    for (const d of this.impls) for (const m of d.methods) if (m.body) scan(m.params, m.body, `method:${d.typeName}.${m.name}`);
    for (const t of this.traits.values())
      for (const m of t.methods) if (m.body) scan(m.params, m.body, `method:${t.name}.${m.name}`);
  }

  /** If `e` is a bare reference to a top-level function used as a value (e.g. the
   *  `logIt` in `apply(logIt, x)`), the callable it denotes — so an IO function
   *  handed to a higher-order function is tracked. Lambdas need no entry here: a
   *  lambda body is walked in place, so IO inside one is already attributed to the
   *  enclosing callable (and caught directly when that is inside a `txn`). */
  private fnValueTarget(e: ast.Expr): { key: string; name: string } | null {
    if (e.kind === "ident" && this.fns.has(e.name)) return { key: `fn:${e.name}`, name: e.name };
    return null;
  }

  /** The callable key a concrete receiver's method resolves to: an explicit impl
   *  method body, else the inherited trait default's body. `null` when the method
   *  has no STM-level body (a native container method like `Set.add`) — such a
   *  call is pure. */
  private concreteMethodKey(typeName: string, method: string): string | null {
    if (this.explicitMethods.has(`${typeName}.${method}`)) return `method:${typeName}.${method}`;
    const trait = this.inheritedDefaults.get(`${typeName}.${method}`);
    if (trait) return `method:${trait}.${method}`;
    return null;
  }

  /** Every callable a `trait`-typed (dynamically dispatched) call to `method` may
   *  reach: the method body of each impl of the trait, plus the trait's own
   *  default body if it has one. Used both for a trait-typed receiver and for a
   *  generic type parameter, whose concrete impl isn't known at the call site. */
  private traitMethodTargets(traitName: string, method: string): string[] {
    const out: string[] = [];
    for (const d of this.implsOfTrait.get(traitName) ?? []) {
      const k = this.concreteMethodKey(d.typeName, method);
      if (k) out.push(k);
    }
    if (this.traitDefaults.has(`${traitName}.${method}`)) out.push(`method:${traitName}.${method}`);
    return out;
  }

  /** The callable key(s) a method call may reach, given the receiver's type. A
   *  concrete receiver resolves to a single body; a trait-typed receiver or a
   *  bounded type parameter fans out over every implementation (sound: the call
   *  is IO if *any* reachable body is). */
  private methodTargets(recv: Type, method: string): string[] {
    if (recv.k === "trait") return this.traitMethodTargets(recv.name, method);
    if (recv.k === "var") return recv.bounds.flatMap((b) => this.traitMethodTargets(b, method));
    // Primitive receivers carry their methods under the prelude's inherent-impl
    // key (`str`/`char`); resolve to that callable so IO tracking sees them.
    if (recv.k === "string" || recv.k === "char") {
      const k = this.concreteMethodKey(recv.k === "string" ? "str" : "char", method);
      return k ? [k] : [];
    }
    if (
      recv.k === "struct" ||
      recv.k === "struct-generic" ||
      recv.k === "enum" ||
      recv.k === "enum-generic" ||
      recv.k === "generic"
    ) {
      const k = this.concreteMethodKey(recv.name, method);
      return k ? [k] : [];
    }
    return [];
  }

  /** Record a resolved call as one or more IO obligations — a callable the call
   *  reaches (the callee itself, plus any IO function passed to a parameter the
   *  callee invokes). Each becomes an edge out of the enclosing callable (so IO
   *  propagates to it) and, inside a `txn`, an obligation validated against the IO
   *  set afterward and reported at its own span. */
  private noteCall(targets: { key: string; name: string; span: Span }[]): void {
    // A re-check pass (see `checkGenericCall`) re-walks already-checked arguments to
    // refine their recorded types; the call graph was recorded on the first pass, so
    // skip it here to avoid double-counting effect edges and obligations.
    if (this.recheckDepth > 0) return;
    if (targets.length === 0) return;
    if (this.currentCallable) {
      let set = this.calleesOf.get(this.currentCallable);
      if (!set) this.calleesOf.set(this.currentCallable, (set = new Set()));
      for (const t of targets) set.add(t.key);
    }
    // `io`: a call inside a `txn` is an obligation (IO is banned there).
    if (this.txnDepth > 0) for (const t of targets) this.txnObligations.push(t);
    // `retry`: a `go`-spawned call roots a new thread with no enclosing `txn`, so it
    // can never be discharged — a root obligation, and it does *not* forward `retry`
    // to the spawning callable.
    if (this.spawningGo) {
      for (const t of targets) this.retryRootObligations.push(t);
    } else if (this.txnDepth === 0) {
      // A call outside any `txn` forwards `retry` up to the enclosing callable...
      if (this.currentCallable) {
        let set = this.retryEdges.get(this.currentCallable);
        if (!set) this.retryEdges.set(this.currentCallable, (set = new Set()));
        for (const t of targets) set.add(t.key);
      }
      // ...and if that callable is `main`, the top level has no `txn` to catch a
      // forwarded `retry` either, so it is also a root obligation.
      if (this.currentCallable === "fn:main") for (const t of targets) this.retryRootObligations.push(t);
    }
    // (A call inside a `txn` and not `go`-spawned is discharged: neither a retry edge
    //  nor a root obligation.)

    // `raises`: a `go`-spawned call raises on its *own* thread, where the spawner's
    // handlers can't see it — so it forwards nothing. Otherwise the call forwards
    // `raises` to the enclosing callable unless a `try` around it discharges the
    // effect, and inside a `txn` it owes an obligation unless a `try` *within the
    // transaction* handles it.
    if (!this.spawningGo) {
      if (this.currentCallable && !this.caughtBy(0)) {
        let set = this.raisesEdges.get(this.currentCallable);
        if (!set) this.raisesEdges.set(this.currentCallable, (set = new Set()));
        for (const t of targets) set.add(t.key);
      }
      if (this.txnDepth > 0 && !this.caughtBy(1)) {
        for (const t of targets) this.txnRaiseObligations.push(t);
      }
    }
  }

  /** Is a raise from the point currently being walked handled by an enclosing `try`?
   *  `minTxnDepth` bounds which handlers count: 0 for "any enclosing handler", 1 for
   *  "a handler that is itself inside the transaction" — a `try` wrapped *around* a
   *  `txn` cannot stop an exception from unwinding out of that transaction. */
  private caughtBy(minTxnDepth: number): boolean {
    return this.handlers.some((h) => h.txnDepth >= minTxnDepth);
  }

  /** The IO obligations of a call to a declared function/method `key`: the callee
   *  itself, plus — for each parameter the callee invokes — the IO function (if
   *  any) passed as that argument. This is where a higher-order call inherits its
   *  callback's effect (`apply(logIt, x)` reaches `logIt`). */
  private callTargets(key: string, name: string, expr: ast.CallExpr): { key: string; name: string; span: Span }[] {
    const targets = [{ key, name, span: expr.span }];
    const invoked = this.invokedParams.get(key);
    if (invoked) {
      for (const i of invoked) {
        const arg = expr.args[i]?.value;
        const t = arg ? this.fnValueTarget(arg) : null;
        if (t) targets.push({ key: t.key, name: t.name, span: arg!.span });
      }
    }
    return targets;
  }

  /** Run `body` with `key` as the enclosing callable, so calls inside it are
   *  recorded as edges out of `key`. Lambdas keep the enclosing key, so IO they
   *  perform is attributed to the function that contains them. The `try` handler
   *  stack is lexical *within* a body, so it starts empty for each callable. */
  private withCallable(key: string, body: () => void): void {
    const saved = this.currentCallable;
    const savedHandlers = this.handlers;
    this.currentCallable = key;
    this.handlers = [];
    try {
      body();
    } finally {
      this.currentCallable = saved;
      this.handlers = savedHandlers;
    }
  }

  /** Compute each effect's set as a fixpoint over the call graph, then enforce that
   *  effect's placement constraint. All four effects run the *same* transitive
   *  closure (`effectClosure`) over a per-effect edge set; only the seeds, edges, and
   *  the final constraint differ:
   *
   *  | effect   | seeds                | edges          | constraint                          |
   *  |----------|----------------------|----------------|-------------------------------------|
   *  | `io`     | `io` builtins/decls  | `calleesOf`    | may not be *called inside* a `txn`  |
   *  | `retry`  | bare `retry`/`orElse`| `retryEdges`   | must reach a `txn` before a root    |
   *  | `raises` | uncaught `raise`     | `raisesEdges`  | may not escape a `txn` uncaught     |
   *  | `spawn`  | a `go` statement     | `calleesOf`    | may not be *called inside* a `txn`  |
   *
   *  `retry`'s and `raises`' edges are masked (a call inside a `txn` is discharged for
   *  `retry`; a call inside a catch-all `try` is discharged for `raises`), which is
   *  what lets each forward multi-hop yet stop at its discharge construct.
   *  Runs after the whole program is walked, when the graph is complete. */
  private checkEffects(): void {
    // `io`: forbidden inside a `txn` (a transaction may abort and retry via OCC, so
    // re-running IO would duplicate its observable effects).
    const io = this.effectClosure(this.ioSeeds, this.calleesOf);
    for (const ob of this.txnObligations) {
      if (io.has(ob.key)) {
        // Trace *why* it's IO: the shortest call path from the flagged callable to
        // the IO function it ultimately reaches, e.g. `a → b → print`. Shown only
        // when there's a chain to explain (a directly-IO callee is self-evident).
        const via = this.effectVia(ob.key, this.ioSeeds, io, this.calleesOf);
        this.error(
          `cannot call \`${ob.name}\` inside a \`txn\`: it performs IO${via}, and a transaction may retry`,
          ob.span
        );
      }
    }

    // `retry`: a retrying callable's `retry` needs an enclosing `txn` to catch it.
    // A call in an un-catchable position — `main`'s top level or a `go`-spawned
    // thread (see `retryRootObligations`) — to a callable that (transitively)
    // retries is therefore an error. Because `retry` forwards along `retryEdges`,
    // this catches multi-hop cases the single-hop check used to miss: a helper that
    // calls a retrying function outside a `txn` is itself retrying, and calling
    // *it* outside a `txn` is flagged just the same.
    const retry = this.effectClosure(this.retrySeeds, this.retryEdges);
    for (const ob of this.retryRootObligations) {
      if (retry.has(ob.key)) {
        const via = this.effectVia(ob.key, this.retrySeeds, retry, this.retryEdges);
        this.error(`cannot call \`${ob.name}\` outside a \`txn\`: it may \`retry\`${via}`, ob.span);
      }
    }
    if (this.retrySeeds.has("fn:main")) {
      this.error("`retry` may only run inside a `txn`", this.retrySeedSpans.get("fn:main")!);
    }

    // `raises`: an optimistic `txn` may abort and re-run, so an exception unwinding
    // out of one raises questions v1 declines to answer (does it discard the
    // transaction? was the error computed from a stale snapshot a retry would fix?).
    // The rule instead mirrors the `io` ban: a raise that escapes a transaction is an
    // error — catch it *inside* the `txn`. A bare `raise` in a transaction is flagged
    // where it is written (see `checkStmt`); a *call* that may raise is flagged here,
    // once the closure is known.
    const raises = this.effectClosure(this.raisesSeeds, this.raisesEdges);
    for (const ob of this.txnRaiseObligations) {
      if (raises.has(ob.key)) {
        const via = this.effectVia(ob.key, this.raisesSeeds, raises, this.raisesEdges);
        this.error(
          `cannot call \`${ob.name}\` inside a \`txn\`: it may \`raise\`${via}, and an exception may not escape a transaction — wrap it in a \`try\`/\`catch\` inside the \`txn\``,
          ob.span
        );
      }
    }

    // `spawn`: the runtime cannot spawn a thread inside a transaction — an
    // optimistic `txn` may abort and re-run, and a re-run would spawn the thread a
    // second time (the spawned thread having already escaped the rollback). A `go`
    // written directly in a `txn` is flagged where it is written (see `checkStmt`);
    // a *call* that reaches one is flagged here, once the closure is known. Nothing
    // discharges `spawn`, so its edges are the plain call graph, exactly like `io`.
    const spawn = this.effectClosure(this.spawnSeeds, this.calleesOf);
    for (const ob of this.txnObligations) {
      if (spawn.has(ob.key)) {
        const via = this.effectVia(ob.key, this.spawnSeeds, spawn, this.calleesOf);
        this.error(
          `cannot call \`${ob.name}\` inside a \`txn\`: it spawns a thread with \`go\`${via}, and a transaction may retry — spawn outside the \`txn\``,
          ob.span
        );
      }
    }

    // Publish each callable's net effect set for the language service (inlay
    // hints). Same four closures, indexed by callable key in canonical order —
    // a callable in none of the sets is simply absent (pure).
    for (const key of new Set([...io, ...retry, ...raises, ...spawn])) {
      const eff: ast.EffectName[] = [];
      if (io.has(key)) eff.push("io");
      if (retry.has(key)) eff.push("retry");
      if (raises.has(key)) eff.push("raises");
      if (spawn.has(key)) eff.push("spawn");
      this.effectsByCallable.set(key, eff);
    }
  }

  /** The transitive closure of `seeds` over `edges`: every callable that reaches a
   *  seed along the given edge relation, seeds included. The one propagation engine
   *  every effect rides — a caller joins the set when any callee is in it. */
  private effectClosure(seeds: Set<string>, edges: Map<string, Set<string>>): Set<string> {
    const set = new Set(seeds);
    for (let changed = true; changed; ) {
      changed = false;
      for (const [caller, callees] of edges) {
        if (set.has(caller)) continue;
        for (const c of callees) {
          if (set.has(c)) {
            set.add(caller);
            changed = true;
            break;
          }
        }
      }
    }
    return set;
  }

  /** A ` (a → b → seed)` trailer explaining *why* `start` has the effect: the
   *  shortest path from `start` to a seed over `edges`, rendered for a diagnostic.
   *  Empty when `start` is itself a seed (self-evident, nothing to trace). */
  private effectVia(start: string, seeds: Set<string>, inSet: Set<string>, edges: Map<string, Set<string>>): string {
    const path = this.effectPath(start, seeds, inSet, edges);
    return path.length > 1 ? ` (${path.map((k) => renderCallable(k)).join(" → ")})` : "";
  }

  /** The shortest call path from `start` to a seed, as callable keys, over `edges`.
   *  Every node on a path to a seed is itself in `inSet`, so the search stays within
   *  it. `[start]` when `start` is itself a seed. BFS, so the path is the shortest. */
  private effectPath(start: string, seeds: Set<string>, inSet: Set<string>, edges: Map<string, Set<string>>): string[] {
    if (seeds.has(start)) return [start];
    const prev = new Map<string, string>();
    const seen = new Set([start]);
    const queue = [start];
    while (queue.length) {
      const cur = queue.shift()!;
      for (const next of edges.get(cur) ?? []) {
        if (!inSet.has(next) || seen.has(next)) continue;
        seen.add(next);
        prev.set(next, cur);
        if (seeds.has(next)) {
          const path = [next];
          for (let n = next; prev.has(n); ) {
            n = prev.get(n)!;
            path.unshift(n);
          }
          return path;
        }
        queue.push(next);
      }
    }
    return [start]; // unreachable when `start` is in the set, but keep the message sane
  }

  /** An enum is a *value type* when every field of every variant is
   *  value-compatible (a scalar, a value-type struct, or another value-type
   *  enum) — so it can be stored inline and hashed by value, exactly like an
   *  all-`key` struct. Computed as a fixpoint because enums may reference each
   *  other: start by assuming every enum qualifies, then drop any with a
   *  non-value-compatible field until nothing changes. (A fieldless enum, and a
   *  recursive one with a base case, both qualify — a tagged union stays finite.) */
  private computeValueTypeEnums(): void {
    for (const name of this.enums.keys()) this.valueTypeEnums.add(name);
    for (let changed = true; changed; ) {
      changed = false;
      for (const name of [...this.valueTypeEnums]) {
        const d = this.enums.get(name)!;
        const ok = d.variants.every((v) =>
          v.fields.every((f) => f.type.kind === "named" && this.isValueCompatible(f.type.name))
        );
        if (!ok) {
          this.valueTypeEnums.delete(name);
          changed = true;
        }
      }
    }
  }

  /** Whether a type *name* can appear in a value type: a scalar, a value-type
   *  struct, or (per the current `valueTypeEnums` estimate) a value-type enum. */
  private isValueCompatible(typeName: string): boolean {
    return (
      typeName === "int" ||
      typeName === "float" ||
      typeName === "bool" ||
      typeName === "string" ||
      typeName === "str" || // alias for `string`
      typeName === "char" ||
      this.valueTypes.has(typeName) ||
      this.valueTypeEnums.has(typeName)
    );
  }

  run(): SemanticIndex {
    this.collectGlobals();
    for (const d of this.decls) {
      if (d.kind === "struct") {
        this.checkTypeParamBounds(d.typeParams);
        if (this.valueTypes.has(d.name)) this.checkValueType(d);
        else this.withTypeVars(d.typeParams, () => {
          for (const f of d.fields) this.resolveType(f.type);
          this.checkFieldDefaults(d.fields, d.nameSpan);
        });
      } else if (d.kind === "enum")
        this.withTypeVars(d.typeParams, () => {
          for (const v of d.variants) {
            for (const f of v.fields) this.resolveType(f.type);
            this.checkFieldDefaults(v.fields, v.nameSpan);
          }
        });
      else if (d.kind === "trait") this.checkTrait(d);
      else if (d.kind === "impl") this.checkImpl(d);
      else this.checkFn(d);
    }
    // The call graph is now complete: infer IO-ness and enforce the `txn` ban.
    this.checkEffects();
    return {
      diagnostics: this.diags,
      refs: this.refs,
      nodeTypes: this.nodeTypes,
      scopes: this.scopes,
      globals: this.globals,
      structFields: this.structFields,
      enumVariants: this.enumVariants,
      typeMethods: this.typeMethods,
      typeParams: this.collectTypeParams(),
      effects: this.effectsByCallable,
      elaboration: this.elab,
      referenceGraph: this.buildReferenceGraph(),
    };
  }

  /** Declared type-parameter names for every generic struct/enum/trait, for the
   *  language service to specialize members (see `SemanticIndex.typeParams`). */
  private collectTypeParams(): Map<string, string[]> {
    const out = new Map<string, string[]>();
    const add = (name: string, tps: ast.TypeParam[]) => { if (tps.length) out.set(name, tps.map((p) => p.name)); };
    for (const d of this.structs.values()) add(d.name, d.typeParams);
    for (const d of this.enums.values()) add(d.name, d.typeParams);
    for (const d of this.traits.values()) add(d.name, d.typeParams);
    return out;
  }

  /** Wire the recorded uses to the definitions they resolve to. Every definition
   *  the index knows — top-level symbols, in-scope bindings, and the members
   *  (fields, variant fields, methods) hung off named types — becomes a node,
   *  keyed by where its name is written; every recorded ref becomes an edge filed
   *  under its target definition. A ref may name a definition not otherwise
   *  collected (e.g. a builtin declared in the prelude), so those are added as
   *  nodes too — the graph stays closed under its edges. */
  private buildReferenceGraph(): ReferenceGraph {
    const defs = new Map<DefId, DefInfo>();
    const uses = new Map<DefId, Span[]>();
    const addDef = (d: DefInfo) => {
      if (located(d.nameSpan)) defs.set(defId(d.nameSpan), d);
    };
    for (const d of this.globals) addDef(d);
    for (const s of this.scopes) for (const d of s.defs) addDef(d);
    for (const fields of this.structFields.values()) for (const f of fields) addDef(f);
    for (const variants of this.enumVariants.values()) for (const v of variants) addDef(v);
    for (const methods of this.typeMethods.values()) for (const m of methods) addDef(m);
    for (const r of this.refs) {
      if (!r.def || !located(r.def.nameSpan)) continue;
      const id = defId(r.def.nameSpan);
      if (!defs.has(id)) defs.set(id, r.def);
      // A ref recorded at the definition's own name span is the declaration site
      // itself (e.g. the LHS of a declaring assignment), not a use — it exists so
      // the binder is hoverable. Don't file it as a use edge, or find-usages with
      // `includeDeclaration: false` would surface the declaration as a use.
      if (defId(r.span) === id) continue;
      let list = uses.get(id);
      if (!list) uses.set(id, (list = []));
      list.push(r.span);
    }
    return { defs, uses };
  }

  /** Index every top-level symbol (and its members) before checking bodies. */
  private collectGlobals(): void {
    for (const d of this.decls) {
      if (d.kind === "struct") {
        const structType: Type = d.typeParams.length
          ? { k: "struct-generic", name: d.name, args: d.typeParams.map((p) => ({ k: "var", name: p.name, bounds: p.bounds.map((b) => b.name) } as Type)) }
          : { k: "struct", name: d.name };
        const kw = d.builtin ? "builtin struct" : "struct";
        const def: DefInfo = {
          name: d.name,
          kind: "struct",
          nameSpan: d.nameSpan,
          type: structType,
          detail: d.typeParams.length
            ? `${kw} ${d.name}<${d.typeParams.map((p) => p.name).join(", ")}>`
            : `${kw} ${d.name}`,
        };
        this.globals.push(def);
        this.globalDefs.set(d.name, def);
        // Field types are resolved with the struct's type parameters in scope, so
        // a field of type `A` reads as a type variable (substituted per instance).
        const fields = this.withTypeVars(d.typeParams, () =>
          d.fields.map(
            (f): DefInfo => ({
              name: f.name,
              kind: "field",
              nameSpan: f.nameSpan,
              type: this.resolveType(f.type),
              detail: `${d.name}.${f.name}: ${typeStr(this.resolveType(f.type))}`,
              container: d.name,
              key: f.key,
            })
          )
        );
        this.structFields.set(d.name, fields);
      } else if (d.kind === "enum") {
        const enumType: Type = d.typeParams.length
          ? { k: "enum-generic", name: d.name, args: d.typeParams.map((p) => ({ k: "var", name: p.name, bounds: p.bounds.map((b) => b.name) } as Type)) }
          : { k: "enum", name: d.name };
        const def: DefInfo = {
          name: d.name,
          kind: "enum",
          nameSpan: d.nameSpan,
          type: enumType,
          detail: d.typeParams.length
            ? `enum ${d.name}<${d.typeParams.map((p) => p.name).join(", ")}>`
            : `enum ${d.name}`,
        };
        this.globals.push(def);
        this.globalDefs.set(d.name, def);
        const variants = this.withTypeVars(d.typeParams, () =>
          d.variants.map((v): DefInfo => {
            const vdef: DefInfo = {
              name: v.name,
              kind: "variant",
              nameSpan: v.nameSpan,
              type: enumType,
              detail: variantSignature(d.name, v, (t) => typeStr(this.resolveType(t))),
              container: d.name,
            };
            // Variant constructors are usable as top-level callees too.
            if (!this.globalDefs.has(v.name)) {
              this.globals.push(vdef);
              this.globalDefs.set(v.name, vdef);
            }
            return vdef;
          })
        );
        this.enumVariants.set(d.name, variants);
      } else if (d.kind === "trait") {
        const traitType: Type = {
          k: "trait",
          name: d.name,
          args: d.typeParams.map((p) => ({ k: "var", name: p.name, bounds: p.bounds.map((b) => b.name) })),
        };
        const def: DefInfo = {
          name: d.name,
          kind: "trait",
          nameSpan: d.nameSpan,
          type: traitType,
          detail: d.typeParams.length
            ? `trait ${d.name}<${d.typeParams.map((p) => p.name).join(", ")}>`
            : `trait ${d.name}`,
        };
        this.globals.push(def);
        this.globalDefs.set(d.name, def);
        // A trait value's members are its method signatures; `Self` is abstract.
        this.currentSelf = UNKNOWN;
        this.withTypeVars(d.typeParams, () =>
          this.typeMethods.set(d.name, d.methods.map((m) => this.methodMember(d.name, m)))
        );
        this.currentSelf = null;
      } else if (d.kind === "impl") {
        // Contribute explicit methods and inherited defaults to the target type's
        // member set, with `Self` resolved to the target type. The impl's own type
        // parameters are bound to variables named after the *target's* declared
        // parameters, so a method signature referencing the impl's `T` (e.g.
        // `f: (T) -> U` in `impl<T> ... for Box<T>`) carries a variable that
        // `members` substitutes with the instance's concrete argument.
        const members = this.typeMethods.get(d.typeName) ?? [];
        this.withTypeVarMap(this.implTargetTypeVars(d), () => {
          // `Self` is resolved *inside* that scope, so it carries those same
          // variables: `impl<T> ... for Box<T>` gives `Self = Box<T>`, which
          // `members` substitutes per instance exactly as it does a `T` or a
          // written-out `Box<T>`. Resolved outside it, the target's arguments
          // would have nothing to bind to and `Self` would come out
          // `Box<unknown>` — a wildcard that accepts any instantiation, so
          // `Box<int>.f(other: Self)` would take a `Box<string>`.
          this.currentSelf = this.resolveTypeQuiet(d.target);
          const trait = this.traits.get(d.trait);
          const overridden = new Set(d.methods.map((m) => m.name));
          if (trait) {
            const traitBindings = new Map<string, Type>();
            trait.typeParams.forEach((tp, i) =>
              traitBindings.set(tp.name, d.traitArgs[i] ? this.resolveTypeQuiet(d.traitArgs[i]!) : UNKNOWN)
            );
            this.withTypeVarMap(traitBindings, () => {
              for (const m of trait.methods) {
                if (m.body && !overridden.has(m.name)) members.push(this.methodMember(d.typeName, m, d.trait));
              }
            });
          }
          for (const m of d.methods) members.push(this.methodMember(d.typeName, m, d.trait));
        });
        this.typeMethods.set(d.typeName, members);
        // Record the trait's type arguments (e.g. `Iterator<int>` -> `[int]`) so
        // `for x in it` and generic-trait lookups can read the element type. Done
        // before any body is checked, so declaration order doesn't matter.
        let byTrait = this.implTraitArgs.get(d.typeName);
        if (!byTrait) this.implTraitArgs.set(d.typeName, (byTrait = new Map()));
        this.withTypeVarMap(this.implTargetTypeVars(d), () =>
          byTrait!.set(d.trait, d.traitArgs.map((a) => this.resolveTypeQuiet(a)))
        );
        this.currentSelf = null;
      } else {
        // Resolve the signature with the fn's own type parameters in scope, so a
        // generic `T` reads as a type variable rather than an unknown type.
        const def: DefInfo = this.withTypeVars(d.typeParams, () => ({
          name: d.name,
          kind: "fn",
          nameSpan: d.nameSpan,
          type: d.returnType ? this.resolveTypeQuiet(d.returnType) : VOID,
          detail: fnSignature(d, (t) => typeStr(this.resolveTypeQuiet(t))),
        }));
        this.globals.push(def);
        this.globalDefs.set(d.name, def);
      }
    }
  }

  /** Build a callable `Member` for a trait/impl method — a `self` receiver plus
   *  the declared params and return type. `container` is the type (impl) or trait
   *  the method belongs to; `viaTrait`, when set, names the trait an impl method
   *  satisfies (shown in its hover). */
  private methodMember(
    container: string,
    m: {
      name: string;
      nameSpan: Span;
      typeParams: ast.TypeParam[];
      params: ast.Param[];
      returnType: ast.TypeExpr | null;
      builtin?: boolean;
    },
    viaTrait?: string
  ): Member {
    // Resolve quietly: unknown-type diagnostics and refs come from `checkTrait` /
    // `checkImplMethod`, so building the member set doesn't double-report them.
    // A generic method's own type parameters are in scope while its signature is
    // built, so `U` reads as a type variable (inferred at each call site).
    const { params, ret } = this.withTypeVars(m.typeParams, () => ({
      params: m.params.map((p) => ({ label: p.name, type: this.resolveTypeQuiet(p.type), default: p.default })),
      ret: m.returnType ? this.resolveTypeQuiet(m.returnType) : VOID,
    }));
    const typeParams = m.typeParams.length ? m.typeParams : undefined;
    return {
      name: m.name,
      kind: "method",
      nameSpan: m.nameSpan,
      type: ret,
      detail: methodDetail(m.name, typeParams, params, ret, viaTrait),
      container,
      params,
      ret: () => ret,
      typeParams,
      viaTrait,
      builtin: m.builtin,
    };
  }

  private error(message: string, span: Span): void {
    // A re-check pass (see `checkGenericCall`) only refines recorded types; it must
    // not re-report diagnostics the first pass already produced.
    if (this.recheckDepth > 0) return;
    // The same type expression is resolved more than once for a function that is
    // both declared and called, so one mistake in a signature could be reported
    // three times. A diagnostic is identified by what it says and where, so an
    // exact repeat is the same finding arriving again, not a second one.
    if (this.diags.some((d) => d.message === message && d.span.start === span.start && d.span.end === span.end)) return;
    this.diags.push({ severity: "error", message, span });
  }

  private newScope(span: Span): Env {
    const scope: ScopeInfo = { span, defs: [], byName: new Map() };
    this.scopes.push(scope);
    return new Env(scope);
  }
  private childScope(parent: Env, span: Span): Env {
    const scope: ScopeInfo = { span, defs: [], byName: new Map() };
    this.scopes.push(scope);
    return new Env(scope, parent);
  }

  private recordType(span: Span, type: Type): Type {
    this.nodeTypes.push({ span, type });
    return type;
  }
  private recordRef(span: Span, def: DefInfo | null, type: Type): void {
    this.refs.push({ span, def, type });
  }

  // -- types --------------------------------------------------------------

  /** A value type is stored inline and hashed by value, so each field must be a
   *  scalar, another value type, or a value-type enum (giving a finite, hashable
   *  layout), and the type may not contain itself. A container or an ordinary
   *  heap/mutable struct — or an enum that carries one — is rejected here. */
  private checkValueType(d: ast.StructDecl): void {
    for (const f of d.fields) {
      const t = this.resolveType(f.type); // records the field-type ref (once)
      const ok =
        t.k === "int" ||
        t.k === "float" ||
        t.k === "bool" ||
        t.k === "string" ||
        t.k === "char" ||
        (t.k === "struct" && this.valueTypes.has(t.name)) ||
        (t.k === "enum" && this.valueTypeEnums.has(t.name));
      if (!ok) {
        const hint =
          t.k === "struct"
            ? ` (mark every field of \`${t.name}\` \`key\` to make it a value type)`
            : t.k === "enum"
              ? ` (enum \`${t.name}\` has a variant field that isn't a scalar or value type)`
              : "";
        this.error(
          `field \`${f.name}\` of value type \`${d.name}\` must be a scalar, value type, or value-type enum, got ${typeStr(t)}${hint}`,
          f.span
        );
      }
    }
    if (this.valueTypeRecursive(d.name)) {
      this.error(
        `value type \`${d.name}\` contains itself, so it has no inline representation`,
        d.nameSpan
      );
    }
  }

  /** Does `root` reach itself by following value-type fields? Such a type could
   *  never be laid out inline. Only value-type fields are followed — non-value
   *  fields are already reported by `checkValueType`. */
  private valueTypeRecursive(root: string): boolean {
    const visit = (name: string, path: Set<string>): boolean => {
      const d = this.structs.get(name);
      if (!d) return false;
      for (const f of d.fields) {
        if (f.type.kind !== "named") continue;
        const fn = f.type.name;
        if (!this.valueTypes.has(fn)) continue;
        if (fn === root) return true;
        if (path.has(fn)) continue;
        if (visit(fn, new Set(path).add(fn))) return true;
      }
      return false;
    };
    return visit(root, new Set([root]));
  }

  private resolveType(te: ast.TypeExpr): Type {
    if (te.kind === "fn") {
      return {
        k: "fn",
        params: te.params.map((p) => this.resolveType(p)),
        ret: this.resolveType(te.ret),
      };
    }
    switch (te.name) {
      case "int":
        return INT;
      case "float":
        return FLOAT;
      case "bool":
        return BOOL;
      case "string":
      case "str": // `str` is an accepted alias for `string`
        return STRING;
      case "char":
        return CHAR;
      case "void":
        return VOID;
    }
    // A type parameter in scope shadows a same-named global type: `Pair<A, B>`'s
    // field `A` is the parameter even when the program also declares `struct A`.
    // Checked before the struct/enum/trait lookups below so the parameter wins.
    {
      const tv = this.typeVars.get(te.name);
      if (tv) return tv;
    }
    if (ast.isGenericContainer(te.name)) {
      const args = containerArgs(te.name, (i) => (te.args[i] ? this.resolveType(te.args[i]!) : undefined));
      const t: Type = { k: "generic", name: te.name, args };
      // Point go-to-definition / hover on the type name at its `builtin struct`
      // declaration in the stdlib (`globalDefs` carries its source `nameSpan`).
      this.recordRef(nameSpan(te.span, te.name), this.globalDefs.get(te.name) ?? null, t);
      return t;
    }
    const def = this.globalDefs.get(te.name);
    if (this.structs.has(te.name)) {
      const structDecl = this.structs.get(te.name)!;
      if (structDecl.typeParams.length) {
        this.checkTypeArity(te, structDecl.typeParams.length);
        const args = structDecl.typeParams.map((_, i) => (te.args[i] ? this.resolveType(te.args[i]!) : UNKNOWN));
        const t: Type = { k: "struct-generic", name: te.name, args };
        this.recordRef(nameSpan(te.span, te.name), def ?? null, t);
        return t;
      }
      this.recordRef(nameSpan(te.span, te.name), def ?? null, { k: "struct", name: te.name });
      return { k: "struct", name: te.name };
    }
    if (this.enums.has(te.name)) {
      const enumDecl = this.enums.get(te.name)!;
      if (enumDecl.typeParams.length) {
        this.checkTypeArity(te, enumDecl.typeParams.length);
        const args = enumDecl.typeParams.map((_, i) => (te.args[i] ? this.resolveType(te.args[i]!) : UNKNOWN));
        const t: Type = { k: "enum-generic", name: te.name, args };
        this.recordRef(nameSpan(te.span, te.name), def ?? null, t);
        return t;
      }
      this.recordRef(nameSpan(te.span, te.name), def ?? null, { k: "enum", name: te.name });
      return { k: "enum", name: te.name };
    }
    if (this.traits.has(te.name)) {
      const trait = this.traits.get(te.name)!;
      if (te.args.length !== trait.typeParams.length)
        this.error(
          `\`${te.name}\` takes ${trait.typeParams.length} type argument(s), but got ${te.args.length}`,
          te.span
        );
      const t: Type = {
        k: "trait",
        name: te.name,
        args: trait.typeParams.map((_, i) => (te.args[i] ? this.resolveType(te.args[i]!) : UNKNOWN)),
      };
      this.recordRef(nameSpan(te.span, te.name), def ?? null, t);
      return t;
    }
    // `Self` — the implementing type — is valid only inside a trait or impl.
    if (te.name === "Self") {
      if (this.currentSelf) return this.currentSelf;
      this.error("`Self` is only valid inside a trait or impl", te.span);
      return UNKNOWN;
    }
    // A generic function's type parameter resolves to its `var` type; a generic
    // impl's parameter is opaque (`unknown`).
    const tv = this.typeVars.get(te.name);
    if (tv) return tv;
    if (this.currentTypeParams.has(te.name)) return UNKNOWN;
    this.error(`unknown type \`${te.name}\``, te.span);
    return UNKNOWN;
  }

  /** Resolve a type without recording refs or diagnostics — for building member
   *  signatures and comparing impl/trait signatures, where the same type
   *  expression is already checked elsewhere. */
  private resolveTypeQuiet(te: ast.TypeExpr): Type {
    if (te.kind === "fn") {
      return {
        k: "fn",
        params: te.params.map((p) => this.resolveTypeQuiet(p)),
        ret: this.resolveTypeQuiet(te.ret),
      };
    }
    switch (te.name) {
      case "int":
        return INT;
      case "float":
        return FLOAT;
      case "bool":
        return BOOL;
      case "string":
      case "str":
        return STRING;
      case "char":
        return CHAR;
      case "void":
        return VOID;
    }
    // A type parameter in scope shadows a same-named global type (see `resolveType`).
    {
      const tv = this.typeVars.get(te.name);
      if (tv) return tv;
    }
    if (ast.isGenericContainer(te.name)) {
      const args = containerArgs(te.name, (i) =>
        te.args[i] ? this.resolveTypeQuiet(te.args[i]!) : undefined
      );
      return { k: "generic", name: te.name, args };
    }
    if (te.name === "Self") return this.currentSelf ?? UNKNOWN;
    if (this.structs.has(te.name)) {
      const structDecl = this.structs.get(te.name)!;
      if (structDecl.typeParams.length) {
        const args = structDecl.typeParams.map((_, i) => (te.args[i] ? this.resolveTypeQuiet(te.args[i]!) : UNKNOWN));
        return { k: "struct-generic", name: te.name, args };
      }
      return { k: "struct", name: te.name };
    }
    if (this.enums.has(te.name)) {
      const enumDecl = this.enums.get(te.name)!;
      if (enumDecl.typeParams.length) {
        const args = enumDecl.typeParams.map((_, i) => te.args[i] ? this.resolveTypeQuiet(te.args[i]!) : UNKNOWN);
        return { k: "enum-generic", name: te.name, args };
      }
      return { k: "enum", name: te.name };
    }
    if (this.traits.has(te.name)) {
      const trait = this.traits.get(te.name)!;
      return {
        k: "trait",
        name: te.name,
        args: trait.typeParams.map((_, i) => (te.args[i] ? this.resolveTypeQuiet(te.args[i]!) : UNKNOWN)),
      };
    }
    return this.typeVars.get(te.name) ?? UNKNOWN;
  }

  private checkFn(fn: ast.FnDecl): void {
    // A `builtin fn` is declared here but implemented natively (see stdlib.ts):
    // it has no body to check, and a call to it is typed from its declaration.
    if (fn.builtin) return;
    // Validate the type parameters' bounds (each must name a real trait), then
    // check the body with them in scope so `T` resolves to its `var` type.
    for (const tp of fn.typeParams) {
      for (const b of tp.bounds) {
        if (!this.traits.has(b.name)) this.error(`unknown trait \`${b.name}\` in bound on \`${tp.name}\``, b.span);
        else this.recordRef(b.span, this.globalDefs.get(b.name) ?? null, { k: "trait", name: b.name, args: [] });
      }
    }
    this.withTypeVars(fn.typeParams, () => {
      const env = this.newScope(fn.body.span);
      // Parameter defaults are checked here, *before* the parameters are in scope, so
      // a default is a closed expression (it may reference globals, not other params).
      // Each is verified against its parameter type; and a required parameter may not
      // follow a defaulted one (defaults must trail, for positional calls).
      this.checkParamDefaults(fn.params, env);
      for (const p of fn.params) {
        const t = this.resolveType(p.type);
        env.declare({
          name: p.name,
          kind: "param",
          nameSpan: p.nameSpan,
          type: t,
          detail: `${p.name}: ${typeStr(t)}`,
        });
      }
      const ret = fn.returnType ? this.resolveType(fn.returnType) : VOID;
      this.withCallable(`fn:${fn.name}`, () => this.checkBlock(fn.body, env, ret));
    });
  }

  /** Check parameter (or field) default expressions against their declared types in
   *  `env` — which must not yet bind the parameters, so a default is closed — and
   *  enforce that a required parameter never follows a defaulted one. */
  private checkParamDefaults(params: ast.Param[], env: Env): void {
    let sawDefault = false;
    for (const p of params) {
      if (p.default) {
        sawDefault = true;
        const t = this.resolveType(p.type);
        const got = this.checkExpr(p.default, env, t);
        if (t.k !== "unknown" && !assignable(t, got, this.implementsTraitType))
          this.error(`default for \`${p.name}\` is ${typeStr(got)}, expected ${typeStr(t)}`, p.default.span);
      } else if (sawDefault) {
        this.error(`required parameter \`${p.name}\` cannot follow one with a default value`, p.nameSpan);
      }
    }
  }

  /** Check a struct's (or variant's) field defaults against their declared types,
   *  in a globals-only scope so a default is a closed expression (it may reference
   *  globals, not other fields). Called with the type's parameters in scope. Unlike
   *  parameters, fields carry no ordering constraint — construction matches by name
   *  and any field may be omitted — so a defaulted field may precede a plain one. */
  private checkFieldDefaults(fields: ast.FieldDecl[], span: Span): void {
    if (!fields.some((f) => f.default)) return;
    const env = this.newScope(span);
    for (const f of fields) {
      if (!f.default) continue;
      const t = this.resolveType(f.type);
      const got = this.checkExpr(f.default, env, t);
      if (t.k !== "unknown" && !assignable(t, got, this.implementsTraitType))
        this.error(`default for field \`${f.name}\` is ${typeStr(got)}, expected ${typeStr(t)}`, f.default.span);
    }
  }

  /** Run `body` with `typeParams` bound as type variables (each carrying its
   *  declared bounds). Restores the previous scope afterward, so nested and
   *  sequential generic functions don't leak parameters into one another. */
  private withTypeVars<R>(typeParams: ast.TypeParam[], body: () => R): R {
    if (typeParams.length === 0) return body();
    const saved = this.typeVars;
    this.typeVars = new Map(saved);
    for (const tp of typeParams) {
      this.typeVars.set(tp.name, { k: "var", name: tp.name, bounds: tp.bounds.map((b) => b.name) });
    }
    try {
      return body();
    } finally {
      this.typeVars = saved;
    }
  }

  /** Run `body` with a pre-built map of type-variable bindings layered over the
   *  current ones (name -> its `Type`). Restores the previous scope afterward. */
  private withTypeVarMap<R>(map: Map<string, Type>, body: () => R): R {
    if (map.size === 0) return body();
    const saved = this.typeVars;
    this.typeVars = new Map(saved);
    for (const [name, t] of map) this.typeVars.set(name, t);
    try {
      return body();
    } finally {
      this.typeVars = saved;
    }
  }

  /** The declared type-parameter names of an `impl` target that this program
   *  substitutes concrete arguments into: a generic struct, enum, or container. */
  private targetTypeParamNames(name: string): string[] {
    if (this.structs.has(name)) return this.structs.get(name)!.typeParams.map((p) => p.name);
    if (this.enums.has(name)) return this.enums.get(name)!.typeParams.map((p) => p.name);
    if (ast.isGenericContainer(name)) return containerTypeParams(name);
    return [];
  }

  /** Map an `impl`'s own type parameters to variables named after the target's
   *  declared parameters, so `impl<A> Trait for Box<A>` binds `A` to the variable
   *  `T` (Box's parameter) that `members` substitutes on. Positional, following
   *  the target's type arguments. Empty when the target isn't a generic struct/enum. */
  private implTargetTypeVars(d: ast.ImplDecl): Map<string, Type> {
    const map = new Map<string, Type>();
    const targetParams = this.targetTypeParamNames(d.target.name);
    d.target.args.forEach((arg, i) => {
      const param = targetParams[i];
      if (arg.kind === "named" && param) map.set(arg.name, { k: "var", name: param, bounds: [] });
    });
    return map;
  }

  // -- traits & impls -----------------------------------------------------

  /** Validate a trait declaration: resolve method types, check default bodies,
   *  and reject duplicate method names. */
  private checkTrait(d: ast.TraitDecl): void {
    this.withTypeVars(d.typeParams, () => {
      const traitSelf: Type = {
        k: "trait",
        name: d.name,
        args: d.typeParams.map((p) => this.typeVars.get(p.name) ?? UNKNOWN),
      };
      this.currentSelf = traitSelf;
      const seen = new Set<string>();
      for (const m of d.methods) {
        if (seen.has(m.name)) this.error(`trait \`${d.name}\` declares method \`${m.name}\` more than once`, m.nameSpan);
        seen.add(m.name);
        this.checkTypeParamBounds(m.typeParams);
        this.checkMethodParamDefaults(m.params, m.typeParams, m.nameSpan);
        // The method's own type parameters (`<U>`) are in scope for its signature
        // and default body.
        this.withTypeVars(m.typeParams, () => {
          for (const p of m.params) this.resolveType(p.type);
          if (m.returnType) this.resolveType(m.returnType);
        });
        if (m.body)
          this.withCallable(`method:${d.name}.${m.name}`, () =>
            this.checkMethodBody({ ...m, body: m.body! }, traitSelf)
          );
      }
      this.currentSelf = null;
    });
  }

  /** Validate that each type parameter's bounds name real traits (and record the
   *  ref for go-to-definition). Shared by generic functions, trait methods, and
   *  impl methods. */
  private checkTypeParamBounds(typeParams: ast.TypeParam[]): void {
    for (const tp of typeParams) {
      for (const b of tp.bounds) {
        if (!this.traits.has(b.name)) this.error(`unknown trait \`${b.name}\` in bound on \`${tp.name}\``, b.span);
        else this.recordRef(b.span, this.globalDefs.get(b.name) ?? null, { k: "trait", name: b.name, args: [] });
      }
    }
  }

  /** Validate an `impl Trait for Type` block: the target type and trait must
   *  exist, the impl must supply exactly the trait's methods with matching
   *  signatures, and each method body is checked with `self` bound to the type.
   *  An *inherent* impl (`impl Type { … }`, no trait — `d.trait === ""`) skips
   *  the trait-conformance step: its methods are simply attached to the type. */
  private checkImpl(d: ast.ImplDecl): void {
    const savedImplVars = this.typeVars;
    this.typeVars = new Map(savedImplVars);
    for (const [name, type] of this.implTargetTypeVars(d)) this.typeVars.set(name, type);
    try {
      const selfType = this.resolveImplTarget(d);
      // `Self` resolves to the concrete target type throughout the impl. Keep the
      // impl's type parameters symbolic so signature matching preserves their
      // relationships (e.g. Iterator<T> is distinct from Iterator<U>).
      this.currentSelf = selfType;
      this.currentTypeParams = new Set(d.typeParams);
      const trait = d.trait === "" ? null : this.traits.get(d.trait);
      if (d.trait === "") {
        // Inherent impl: nothing to conform to.
      } else if (!trait) {
        this.error(`unknown trait \`${d.trait}\``, d.traitSpan);
      } else {
        this.recordRef(d.traitSpan, this.globalDefs.get(d.trait) ?? null, {
          k: "trait",
          name: d.trait,
          args: d.traitArgs.map((a) => this.resolveTypeQuiet(a)),
        });
        // Bind the trait's type parameters separately from the impl's. They may
        // use the same source name (`T`) while denoting different variables.
        const traitBindings = new Map<string, Type>();
        if (d.traitArgs.length !== trait.typeParams.length) {
          this.error(
            `\`${trait.name}\` takes ${trait.typeParams.length} type argument(s), but got ${d.traitArgs.length}`,
            d.traitSpan
          );
        }
        trait.typeParams.forEach((tp, i) => {
          const arg = d.traitArgs[i];
          traitBindings.set(tp.name, arg ? this.resolveType(arg) : UNKNOWN);
        });
        this.checkImplMatchesTrait(d, trait, traitBindings);
      }
      for (const m of d.methods)
        this.withCallable(`method:${d.typeName}.${m.name}`, () => this.checkImplMethod(m, selfType));
    } finally {
      this.currentTypeParams = new Set();
      this.currentSelf = null;
      this.typeVars = savedImplVars;
    }
  }

  /** Resolve an impl's target type — a struct, value type, enum, or a generic
   *  container (`Set<T>`/`Dict<K,V>`/`Table<T>`) — recording a ref for
   *  go-to-definition on the head name. A generic container's type arguments must
   *  be the impl's type parameters: the impl applies to every instantiation, so
   *  `self` is `Set<unknown>` (the element type is opaque). */
  private resolveImplTarget(d: ast.ImplDecl): Type {
    const t = d.target;
    if (ast.isGenericContainer(t.name)) {
      const params = new Set(d.typeParams);
      for (const a of t.args) {
        if (a.kind !== "named" || !params.has(a.name))
          this.error(
            `\`impl\` target \`${t.name}\`'s type arguments must be its type parameters (e.g. \`${t.name}<${d.typeParams.join(", ") || "T"}>\`), got \`${typeExprStr(a)}\``,
            a.span
          );
      }
      // Element types are opaque here — the impl covers all instantiations.
      return { k: "generic", name: t.name, args: containerArgs(t.name, () => undefined) };
    }
    const def = this.globalDefs.get(t.name) ?? null;
    if (this.structs.has(t.name)) {
      const structDecl = this.structs.get(t.name)!;
      if (structDecl.typeParams.length) {
        // Generic struct: the target must be written out with its type parameters.
        // `impl Box` for a `struct Box<T>` left `T` as `unknown`, so `self.value`
        // recorded `unknown` instead of the parameter and the backend had nothing
        // to substitute — the error surfaced far downstream, in monomorphization.
        if (t.args.length !== structDecl.typeParams.length)
          this.error(
            `\`impl\` target \`${t.name}\` takes ${structDecl.typeParams.length} type argument(s), but got ${t.args.length} (e.g. \`impl<${structDecl.typeParams.map((p) => p.name).join(", ")}> ${t.name}<${structDecl.typeParams.map((p) => p.name).join(", ")}>\`)`,
            d.typeSpan
          );
        const implParams = new Set(d.typeParams);
        for (const a of t.args) {
          if (a.kind !== "named" || !implParams.has(a.name))
            this.error(
              `\`impl\` target \`${t.name}\`'s type arguments must be its type parameters (e.g. \`${t.name}<${d.typeParams.join(", ") || structDecl.typeParams.map((p) => p.name).join(", ")}>\`), got \`${typeExprStr(a)}\``,
              a.span
            );
        }
        // `self` is `Struct<T>` with the impl's type parameters kept as *variables*
        // (not erased to `unknown`), so a member access through `self` records the
        // parameter — `self.value: Option<T>`, `self.get(): T` — which the backend
        // substitutes per instantiation, rather than recording `Option<unknown>`.
        const implType: Type = {
          k: "struct-generic",
          name: t.name,
          args: structDecl.typeParams.map((_, i) => (t.args[i] ? this.resolveTypeQuiet(t.args[i]!) : UNKNOWN)),
        };
        this.recordRef(d.typeSpan, def, implType);
        return implType;
      }
      if (t.args.length || d.typeParams.length)
        this.error(`type \`${t.name}\` is not generic, so \`impl\` for it takes no type parameters`, d.typeSpan);
      this.recordRef(d.typeSpan, def, { k: "struct", name: t.name });
      return { k: "struct", name: t.name };
    }
    if (this.enums.has(t.name)) {
      const enumDecl = this.enums.get(t.name)!;
      if (enumDecl.typeParams.length) {
        // Generic enum: the target must be written out with its type parameters.
        // `impl Box` for a `struct Box<T>` left `T` as `unknown`, so `self.value`
        // recorded `unknown` instead of the parameter and the backend had nothing
        // to substitute — the error surfaced far downstream, in monomorphization.
        if (t.args.length !== enumDecl.typeParams.length)
          this.error(
            `\`impl\` target \`${t.name}\` takes ${enumDecl.typeParams.length} type argument(s), but got ${t.args.length} (e.g. \`impl<${enumDecl.typeParams.map((p) => p.name).join(", ")}> ${t.name}<${enumDecl.typeParams.map((p) => p.name).join(", ")}>\`)`,
            d.typeSpan
          );
        const implParams = new Set(d.typeParams);
        for (const a of t.args) {
          if (a.kind !== "named" || !implParams.has(a.name))
            this.error(
              `\`impl\` target \`${t.name}\`'s type arguments must be its type parameters (e.g. \`${t.name}<${d.typeParams.join(", ") || enumDecl.typeParams.map((p) => p.name).join(", ")}>\`), got \`${typeExprStr(a)}\``,
              a.span
            );
        }
        // Keep the impl's type parameters as variables (see the struct case), so a
        // member access through `self` records the parameter rather than `unknown`.
        const implType: Type = {
          k: "enum-generic",
          name: t.name,
          args: enumDecl.typeParams.map((_, i) => (t.args[i] ? this.resolveTypeQuiet(t.args[i]!) : UNKNOWN)),
        };
        this.recordRef(d.typeSpan, def, implType);
        return implType;
      }
      if (t.args.length || d.typeParams.length)
        this.error(`type \`${t.name}\` is not generic, so \`impl\` for it takes no type parameters`, d.typeSpan);
      this.recordRef(d.typeSpan, def, { k: "enum", name: t.name });
      return { k: "enum", name: t.name };
    }
    // A primitive target (`str`/`char`/…): an inherent impl attaches methods to a
    // built-in scalar. It takes no type parameters. This is how the prelude hangs
    // `len`/`charAt`/`split` on `str` and `code`/`isDigit` on `char`.
    const prim = primitiveType(t.name);
    if (prim) {
      if (t.args.length || d.typeParams.length)
        this.error(`type \`${t.name}\` is not generic, so \`impl\` for it takes no type parameters`, d.typeSpan);
      return prim;
    }
    this.error(`cannot \`impl\` for unknown type \`${t.name}\``, d.typeSpan);
    return UNKNOWN;
  }

  /** A generic type named in a type position must say what it is generic over.
   *  Omitting the arguments silently filled them with `unknown`, which erases the
   *  type rather than rejecting it: `fn peek(b: Box)` type-checked, ran, and
   *  compiled to Go that dereferenced nil. Traits have always been checked this
   *  way; structs and enums now are too. */
  private checkTypeArity(te: ast.NamedType, expected: number): void {
    if (te.args.length === expected) return;
    this.error(
      `\`${te.name}\` takes ${expected} type argument(s), but got ${te.args.length}`,
      te.span
    );
  }

  /** Every body-less trait method must be implemented with a matching signature;
   *  default methods may be omitted, and the impl may not declare unknown methods. */
  private checkImplMatchesTrait(
    d: ast.ImplDecl,
    trait: ast.TraitDecl,
    traitBindings: Map<string, Type>
  ): void {
    const implByName = new Map(d.methods.map((m) => [m.name, m]));
    for (const sig of trait.methods) {
      const m = implByName.get(sig.name);
      if (!m && !sig.body) {
        this.error(`impl of \`${trait.name}\` for \`${d.typeName}\` is missing method \`${sig.name}\``, d.typeSpan);
        continue;
      }
      if (m) this.checkSignatureMatch(trait, sig, m, traitBindings);
    }
    const declared = new Set(trait.methods.map((m) => m.name));
    for (const m of d.methods) {
      if (!declared.has(m.name)) this.error(`\`${m.name}\` is not a method of trait \`${trait.name}\``, m.nameSpan);
    }
  }

  /** Check that an impl method's parameter and return types match the trait's
   *  declared signature (the `self` receiver is implicit on both). */
  private checkSignatureMatch(
    trait: ast.TraitDecl,
    sig: ast.TraitMethodSig,
    m: ast.FnDecl,
    traitBindings: Map<string, Type>
  ): void {
    if (m.params.length !== sig.params.length) {
      this.error(
        `method \`${m.name}\` takes ${m.params.length} parameter(s), but trait \`${trait.name}\` declares ${sig.params.length}`,
        m.nameSpan
      );
      return;
    }
    if (m.typeParams.length !== sig.typeParams.length) {
      this.error(
        `method \`${m.name}\` declares ${m.typeParams.length} type parameter(s), but trait \`${trait.name}\` declares ${sig.typeParams.length}`,
        m.nameSpan
      );
      return;
    }
    // Bind the trait sig's and the impl's own type parameters positionally to the
    // *same* synthetic variables, so a `<U>` on each side compares equal even if
    // the impl renames it. Layered over the trait-argument bindings already in
    // scope (e.g. the trait's `T` -> `int`).
    const saved = this.typeVars;
    const traitVars = new Map(saved);
    for (const [name, type] of traitBindings) traitVars.set(name, type);
    sig.typeParams.forEach((tp, i) =>
      traitVars.set(tp.name, { k: "var", name: `μ${i}`, bounds: tp.bounds.map((b) => b.name) })
    );
    const implVars = new Map(saved);
    m.typeParams.forEach((tp, i) =>
      implVars.set(tp.name, { k: "var", name: `μ${i}`, bounds: tp.bounds.map((b) => b.name) })
    );
    try {
      sig.params.forEach((sp, i) => {
        this.typeVars = traitVars;
        const want = this.resolveTypeQuiet(sp.type);
        this.typeVars = implVars;
        const got = this.resolveTypeQuiet(m.params[i]!.type);
        if (!this.sameType(want, got))
          this.error(
            `parameter \`${m.params[i]!.name}\` of \`${m.name}\` has type ${typeStr(got)}, but trait \`${trait.name}\` declares ${typeStr(want)}`,
            m.params[i]!.nameSpan
          );
      });
      // The return type is *covariant*: an impl may return a subtype of the
      // trait's declared return — most usefully a concrete type where the trait
      // declares a trait type it implements (e.g. `clone(self) Cloneable`
      // implemented as `clone(self) Circle`). Parameters stay invariant (exact),
      // which keeps this sound. Covariance is limited to a trait-typed declared
      // return so scalars/containers aren't silently widened.
      this.typeVars = traitVars;
      const wantRet = sig.returnType ? this.resolveTypeQuiet(sig.returnType) : VOID;
      this.typeVars = implVars;
      const gotRet = m.returnType ? this.resolveTypeQuiet(m.returnType) : VOID;
      const retOk =
        this.sameType(wantRet, gotRet) ||
        (wantRet.k === "trait" && assignable(wantRet, gotRet, this.implementsTraitType));
      if (!retOk)
        this.error(
          `method \`${m.name}\` returns ${typeStr(gotRet)}, but trait \`${trait.name}\` declares ${typeStr(wantRet)}`,
          m.nameSpan
        );
    } finally {
      this.typeVars = saved;
    }
  }

  /** Check one impl method body, with `self` bound to the impl's target type. A
   *  generic method's own type parameters (`<U>`) are in scope throughout. */
  private checkImplMethod(fn: ast.FnDecl, selfType: Type): void {
    // A `builtin` method is declared here but implemented natively (see stdlib.ts):
    // like a `builtin fn` free function it has no body to check; its signature was
    // already matched against the trait in `checkImplMatchesTrait`.
    if (fn.builtin) return;
    this.checkTypeParamBounds(fn.typeParams);
    this.checkMethodParamDefaults(fn.params, fn.typeParams, fn.nameSpan);
    this.checkMethodBody(fn, selfType);
  }

  /** Validate a method's parameter defaults — the same rules as free-function
   *  parameters: each default is checked (in a globals-only scope, so it is a closed
   *  expression that may reference globals but not `self` or another parameter) and a
   *  required parameter may not follow a defaulted one. Run with the method's own type
   *  parameters in scope so a generic parameter type resolves. At the call site the
   *  default is filled from the method's resolved member (see `methodMember`), so it
   *  works for both direct and dynamically-dispatched calls. */
  private checkMethodParamDefaults(params: ast.Param[], typeParams: ast.TypeParam[], span: Span): void {
    if (!params.some((p) => p.default)) return;
    this.withTypeVars(typeParams, () => this.checkParamDefaults(params, this.newScope(span)));
  }

  private checkMethodBody(
    fn: { nameSpan: Span; self?: Span; typeParams: ast.TypeParam[]; params: ast.Param[]; returnType: ast.TypeExpr | null; body: ast.Block },
    selfType: Type
  ): void {
    this.withTypeVars(fn.typeParams, () => {
      const env = this.newScope(fn.body.span);
      env.declare({
        name: "self",
        kind: "param",
        nameSpan: fn.self ?? fn.nameSpan,
        type: selfType,
        detail: `self: ${typeStr(selfType)}`,
      });
      for (const p of fn.params) {
        const t = this.resolveType(p.type);
        env.declare({ name: p.name, kind: "param", nameSpan: p.nameSpan, type: t, detail: `${p.name}: ${typeStr(t)}` });
      }
      const ret = fn.returnType ? this.resolveType(fn.returnType) : VOID;
      this.checkBlock(fn.body, env, ret);
    });
  }

  /** Exact structural type identity — for signature matching, where `int` and
   *  `float` are distinct (unlike assignability). Lenient only on `unknown`, so a
   *  mistyped annotation isn't reported twice. */
  private sameType(a: Type, b: Type): boolean {
    return sameTypeExact(a, b);
  }

  // -- statements ---------------------------------------------------------

  private checkBlock(block: ast.Block, env: Env, ret: Type): void {
    const inner = this.childScope(env, block.span);
    for (const s of block.stmts) this.checkStmt(s, inner, ret);
  }

  private checkStmt(stmt: ast.Stmt, env: Env, ret: Type): void {
    switch (stmt.kind) {
      case "block":
        return this.checkBlock(stmt, env, ret);
      case "let": {
        // A type annotation is the expected type — bidirectional inference for the
        // initializer (`x: Option<int> = None()` fixes `T` from the annotation).
        const annotated = stmt.type ? this.resolveType(stmt.type) : undefined;
        const t = this.checkExpr(stmt.value, env, annotated);
        const declared = annotated ?? t;
        env.declare({
          name: stmt.name,
          kind: "let",
          nameSpan: stmt.nameSpan,
          type: declared,
          detail: `${stmt.name}: ${typeStr(declared)}`,
        });
        return;
      }
      case "assign": {
        // Reassigning an existing binding: its current type is the expected type for
        // the right-hand side (so `x = None()` where `x: Option<int>` stays typed).
        const reassignExpected =
          stmt.target.kind === "ident" ? env.lookup(stmt.target.name)?.type : undefined;
        const rhs = this.checkExpr(stmt.value, env, reassignExpected);
        if (stmt.target.kind === "ident") {
          const existing = env.lookup(stmt.target.name);
          if (existing === undefined) {
            const def: DefInfo = {
              name: stmt.target.name,
              kind: "let",
              nameSpan: stmt.target.span,
              type: rhs,
              detail: `${stmt.target.name}: ${typeStr(rhs)}`,
            };
            env.declare(def);
            // Record the binding site as a reference to its own definition so
            // hovering the LHS of a declaring assignment shows its type, just as
            // a reassignment's LHS already does (the `else` branch below).
            this.recordRef(stmt.target.span, def, rhs);
          } else {
            this.recordRef(stmt.target.span, existing, existing.type);
            this.recordType(stmt.target.span, existing.type);
          }
        } else if (stmt.target.kind === "field") {
          // Field assignment: resolve the field (evaluating the receiver once,
          // recording its refs), then reject mutation of a value type — its
          // fields can only be replaced wholesale, never assigned in place.
          const fa = stmt.target;
          const objType = this.checkExpr(fa.object, env);
          const member = this.members(objType).find((m) => m.name === fa.field);
          if (member && !member.params) {
            this.recordRef(fa.fieldSpan, member, member.type);
            // Record the field lvalue's type too — a consumer (e.g. lockplan's
            // footprint analysis) reads the assigned field's type from here.
            this.elab.types.set(fa, member.type);
          }
          else if (objType.k === "struct" || objType.k === "struct-generic")
            this.error(`\`${objType.name}\` has no field \`${fa.field}\``, fa.span);
          if (objType.k === "struct" && this.valueTypes.has(objType.name))
            this.error(
              `cannot assign to field \`${fa.field}\` of \`${objType.name}\`: value types are immutable`,
              stmt.span
            );
        } else {
          this.checkExpr(stmt.target, env);
        }
        return;
      }
      case "exprStmt":
        this.checkExpr(stmt.expr, env);
        return;
      case "return": {
        // Inside a block-bodied lambda, returns are collected in a sink to infer the
        // lambda's result type; the expected type (if the lambda is checked against a
        // known function type) is the sink's. Otherwise the function's declared
        // return type is the expected type for the returned expression.
        const sink = this.returnSinks[this.returnSinks.length - 1];
        const wantRet = sink ? sink.expected : ret;
        const t = stmt.value ? this.checkExpr(stmt.value, env, wantRet) : VOID;
        if (sink) sink.types.push(t);
        else if (stmt.value && !assignable(ret, t, this.implementsTraitType))
          this.error(`returning ${typeStr(t)} from a function returning ${typeStr(ret)}`, stmt.span);
        return;
      }
      case "if":
        this.expectBool(this.checkExpr(stmt.cond, env), stmt.cond.span);
        this.checkBlock(stmt.then, env, ret);
        if (stmt.else) {
          if (stmt.else.kind === "if") this.checkStmt(stmt.else, env, ret);
          else this.checkBlock(stmt.else, env, ret);
        }
        return;
      case "while":
        this.expectBool(this.checkExpr(stmt.cond, env), stmt.cond.span);
        this.checkBlock(stmt.body, env, ret);
        return;
      case "for": {
        const { elem, res } = this.resolveIterable(stmt.iterable, env);
        this.elab.loops.set(stmt, res);
        const inner = this.childScope(env, stmt.body.span);
        inner.declare({
          name: stmt.varName,
          kind: "loop-var",
          nameSpan: stmt.varSpan,
          type: elem,
          detail: `${stmt.varName}: ${typeStr(elem)}`,
        });
        this.checkBlock(stmt.body, inner, ret);
        return;
      }
      case "match":
        this.checkMatch(stmt, env, ret);
        return;
      case "go": {
        // A `go` seeds the `spawn` effect for the enclosing callable, which then may
        // not be called inside a `txn` (see `checkEffects`) — a transaction may abort
        // and re-run, and the re-run would spawn a second thread while the first has
        // already escaped the rollback. Written *directly* in a transaction it is an
        // error outright, reported here (and not seeded, so every caller of this
        // function isn't flagged too — the same shape as a bare `raise` in a `txn`).
        if (this.txnDepth > 0) {
          this.error(
            "cannot spawn a thread (`go`) inside a `txn`: a transaction may abort and re-run, which would spawn the thread again — move the `go` outside the transaction",
            stmt.span
          );
        } else if (this.currentCallable) {
          this.spawnSeeds.add(this.currentCallable);
        }
        // The spawned call runs on a *new* thread, outside the current transaction, so
        // it doesn't count as IO performed within one. It also has no enclosing `txn`
        // to catch a `retry`, so `spawningGo` routes its calls to root obligations
        // (see `noteCall`) instead of forwarding `retry`.
        const savedTxn = this.txnDepth;
        const savedGo = this.spawningGo;
        this.txnDepth = 0;
        this.spawningGo = true;
        this.checkExpr(stmt.call, env);
        this.txnDepth = savedTxn;
        this.spawningGo = savedGo;
        return;
      }
      case "txn":
        this.txnDepth++;
        this.checkBlock(stmt.body, env, ret);
        this.txnDepth--;
        return;
      case "retry":
        // A `retry` inside a `txn` block is consumed by that transaction. A *bare*
        // `retry` (outside any `txn` here) makes the enclosing callable
        // retrying — it may then only be called inside a `txn` (enforced in
        // checkEffects, the dual of the `io`-in-`txn` ban). This is what lets
        // `retry` fire from a method called in a caller's `txn` (e.g. `Future.get`).
        if (this.txnDepth === 0 && this.currentCallable) {
          this.retrySeeds.add(this.currentCallable);
          if (!this.retrySeedSpans.has(this.currentCallable))
            this.retrySeedSpans.set(this.currentCallable, stmt.span);
        }
        return;
      case "raise":
        this.checkRaise(stmt, env);
        return;
      case "try":
        this.checkTry(stmt, env, ret);
        return;
      case "assert": {
        this.expectBool(this.checkExpr(stmt.cond, env), stmt.cond.span);
        if (stmt.message) {
          const mt = this.checkExpr(stmt.message, env);
          if (mt.k !== "string" && mt.k !== "unknown")
            this.error(`an assert message must be a string, got ${typeStr(mt)}`, stmt.message.span);
        }
        return;
      }
    }
  }

  /** `raise e` / `raise e from cause`: both values must implement the built-in
   *  `Error` trait. The raise seeds the `raises` effect for the enclosing callable
   *  unless a `try` around it catches this error type; inside a `txn` an uncaught
   *  raise is an error outright (an exception may not unwind out of a transaction —
   *  see `checkEffects`). */
  private checkRaise(stmt: ast.RaiseStmt, env: Env): void {
    const t = this.checkExpr(stmt.value, env);
    this.expectError(t, stmt.value.span, "raise");
    if (stmt.cause) {
      this.expectError(this.checkExpr(stmt.cause, env), stmt.cause.span, "raise … from");
    }
    if (this.txnDepth > 0 && !this.caughtBy(1)) {
      this.error(
        "a `raise` may not escape a `txn`: an exception unwinding out of a transaction would discard it, so catch it with a `try`/`catch` inside the `txn`",
        stmt.span
      );
      return; // already reported here; don't also flag every caller of this function
    }
    if (!this.caughtBy(0) && this.currentCallable) this.raisesSeeds.add(this.currentCallable);
  }

  /** `try { … } catch e { … }`: the body is walked with this `try` pushed on the
   *  handler stack (so raises inside it are discharged), then the handler body is
   *  walked with the error bound as `Error` — *outside* the handler, since a `try`
   *  does not catch what its own `catch` raises. That is what makes a `raise e`
   *  re-raise in the handler propagate, and re-seed the `raises` effect. */
  private checkTry(stmt: ast.TryStmt, env: Env, ret: Type): void {
    this.handlers.push({ txnDepth: this.txnDepth });
    try {
      this.checkBlock(stmt.body, env, ret);
    } finally {
      this.handlers.pop();
    }

    const inner = this.childScope(env, stmt.handler.body.span);
    inner.declare({
      name: stmt.handler.name,
      kind: "let",
      nameSpan: stmt.handler.nameSpan,
      type: ERROR_TRAIT,
      detail: `${stmt.handler.name}: ${typeStr(ERROR_TRAIT)}`,
    });
    this.checkBlock(stmt.handler.body, inner, ret);
  }

  /** Require a raised type to implement the built-in `Error` trait (the `Error`
   *  trait type itself trivially does — which is what lets a handler's binding be
   *  re-raised). An `unknown` passes: a type error was already reported for whatever
   *  produced it. */
  private expectError(t: Type, span: Span, what: string): void {
    if (t.k === "unknown") return;
    if (t.k === "trait" && t.name === "Error") return;
    const name = this.implName(t);
    if (name !== null && this.implementsTrait(name, "Error")) return;
    this.error(
      `cannot \`${what}\` ${typeStr(t)}: it does not implement \`Error\` (add \`impl Error for ${typeStr(t)} { fn message(self) string { … } }\`)`,
      span
    );
  }

  private iterElemType(iterable: ast.Expr, env: Env): Type {
    return this.resolveIterable(iterable, env).elem;
  }

  /** Resolve a `for` loop's iterable: its element type *and* how it is driven —
   *  a built-in `container`'s native iteration, or a user type that implements
   *  `Iterable` (get a fresh iterator via `iter()`) or `Iterator` (drive `next()`).
   *  A range is not a case here: `0..n` desugared to a `Range` construction in the
   *  parser, so it arrives as an ordinary `Iterable` struct. Iterability is decided
   *  by *trait implementation*, never a method name, so every backend draws the same
   *  conclusion the checker does. */
  private resolveIterable(iterable: ast.Expr, env: Env): { elem: Type; res: IterResolution } {
    const t = this.checkExpr(iterable, env);
    if (t.k === "generic") {
      // A container is iterable iff it has an `Iterable` impl (Set/Dict/List do;
      // `Future` does not) — read its element off that impl like any other type.
      const elem = this.iterableElem(t);
      if (elem) return { elem, res: { kind: "container", container: t.name, elem } };
      // No `Iterable` impl (a `Future<T>`): fall through to "not iterable" below.
    }
    if (t.k === "trait" && (t.name === "Iterable" || t.name === "Iterator")) {
      const elem = t.args[0] ?? UNKNOWN;
      // A trait-typed value carries its protocol directly: an `Iterable` value is
      // driven through `iter()`, an `Iterator` value through `next()`.
      const kind = t.name === "Iterable" ? "iterable" : "iterator";
      return { elem, res: { kind, typeName: t.name, elem } };
    }
    if (t.k === "struct" || t.k === "struct-generic" || t.k === "enum" || t.k === "enum-generic") {
      const elem = this.iterableElem(t);
      if (elem) {
        const name = t.name;
        const kind = this.implementsTrait(name, "Iterable") ? "iterable" : "iterator";
        return { elem, res: { kind, typeName: name, elem } };
      }
    }
    if (t.k !== "unknown") this.error(`${typeStr(t)} is not iterable`, iterable.span);
    return { elem: UNKNOWN, res: { kind: "unknown", elem: UNKNOWN } };
  }

  /** The loop-element type declared by a user type's `Iterable<T>` or
   *  `Iterator<T>` implementation. */
  private iterableElem(type: Type): Type | null {
    return this.traitArgsFor(type, "Iterable")?.[0] ?? this.traitArgsFor(type, "Iterator")?.[0] ?? null;
  }

  /** Trait arguments implemented by a concrete type, specialized with the
   *  concrete type's own arguments. */
  private traitArgsFor(type: Type, traitName: string): Type[] | null {
    if (
      type.k !== "struct" &&
      type.k !== "struct-generic" &&
      type.k !== "enum" &&
      type.k !== "enum-generic" &&
      type.k !== "generic"
    )
      return null;
    const template = this.implTraitArgs.get(type.name)?.get(traitName);
    if (!template) return null;
    if (type.k !== "struct-generic" && type.k !== "enum-generic" && type.k !== "generic") return template;
    const subst = new Map<string, Type>();
    this.targetTypeParamNames(type.name).forEach((name, i) => subst.set(name, type.args[i] ?? UNKNOWN));
    return template.map((arg) => this.substitutePartial(arg, subst));
  }

  private checkMatch(stmt: ast.MatchStmt, env: Env, ret: Type): void {
    const subj = this.checkExpr(stmt.subject, env);
    if (subj.k === "unknown") {
      for (const arm of stmt.arms) this.checkBlock(arm.body, this.childScope(env, arm.body.span), ret);
      return;
    }
    if (subj.k !== "enum" && subj.k !== "enum-generic") {
      this.error(`match subject has type ${typeStr(subj)}, not an enum`, stmt.subject.span);
      return;
    }
    const decl = this.enums.get(subj.name)!;
    // Build a substitution map from the enum's type params to the concrete args
    // in this instantiation (e.g. `T` -> `int` for `Option<int>`).
    const subst = new Map<string, Type>();
    if (subj.k === "enum-generic") {
      decl.typeParams.forEach((p, i) => subst.set(p.name, subj.args[i] ?? UNKNOWN));
    }
    // Track coverage so an inexhaustive `match` (one that omits a variant and has
    // no `_`) is a compile error, and so arms after a `_` are flagged unreachable.
    const covered = new Set<string>();
    let sawWildcard = false;
    for (const arm of stmt.arms) {
      const armEnv = this.childScope(env, arm.body.span);
      if (arm.wildcard) {
        // `_` matches any variant, so anything after it can never run.
        if (sawWildcard) this.error("unreachable match arm after `_`", arm.span);
        // A wildcard binds nothing — `_(x)` is meaningless.
        if (arm.bindings.length > 0) {
          this.error("wildcard pattern `_` cannot bind fields", arm.bindings[0]!.fieldSpan);
        }
        sawWildcard = true;
        this.checkBlock(arm.body, armEnv, ret);
        continue;
      }
      const variant = decl.variants.find((v) => v.name === arm.variant);
      if (sawWildcard) this.error("unreachable match arm after `_`", arm.span);
      if (!variant) {
        this.error(`\`${subj.name}\` has no variant \`${arm.variant}\``, arm.span);
      } else {
        covered.add(variant.name);
        this.recordRef(arm.variantSpan, this.globalDefs.get(arm.variant) ?? null, subj);
        arm.bindings.forEach((b) => {
          const field = variant.fields.find((f) => f.name === b.field);
          if (!field) this.error(`variant \`${arm.variant}\` has no field \`${b.field}\``, b.fieldSpan);
          const rawBt = field ? this.withTypeVars(decl.typeParams, () => this.resolveType(field.type)) : UNKNOWN;
          const bt = this.substitute(rawBt, subst);
          // When the binder is renamed, the field-name token is a distinct span
          // from the local; record its type so hovers work on both halves.
          if (b.renamed) this.recordType(b.fieldSpan, bt);
          const bspan = b.nameSpan ?? arm.variantSpan;
          const def: DefInfo = {
            name: b.name,
            kind: "binding",
            nameSpan: bspan,
            type: bt,
            detail: `${b.name}: ${typeStr(bt)}`,
          };
          armEnv.declare(def);
          // Record the binder itself as a reference to its own definition so
          // hovering the pattern binding (e.g. `index` in `IndexScan(index, key)`)
          // shows its type, matching how a use of the binding hovers.
          this.recordRef(bspan, def, bt);
        });
      }
      this.checkBlock(arm.body, armEnv, ret);
    }
    // A `match` must cover every variant, either by naming each one or with a
    // trailing `_`. Report the ones left out.
    if (!sawWildcard) {
      const missing = decl.variants.filter((v) => !covered.has(v.name)).map((v) => v.name);
      if (missing.length > 0) {
        const list = missing.map((n) => `\`${n}\``).join(", ");
        const noun = missing.length === 1 ? "variant" : "variants";
        this.error(`match on \`${subj.name}\` is not exhaustive: missing ${noun} ${list} (add them or a \`_\` arm)`, stmt.span);
      }
    }
  }

  // -- expressions --------------------------------------------------------

  /** Check an expression and record its type. `expected`, when the context supplies
   *  one (a `let`/return annotation, a call argument's parameter type), is the type
   *  the expression is checked *against* — used for local bidirectional inference so
   *  an under-determined constructor like `None()` resolves its type arguments from
   *  context instead of collapsing to `unknown`. Most expressions ignore it. */
  private checkExpr(expr: ast.Expr, env: Env, expected?: Type): Type {
    const t = this.inferExpr(expr, env, expected);
    this.recordType(expr.span, t); // span-keyed, for hovers
    this.elab.types.set(expr, t); // node-keyed, for backends
    return t;
  }

  private inferExpr(expr: ast.Expr, env: Env, expected?: Type): Type {
    switch (expr.kind) {
      case "int":
        return INT;
      case "float":
        return FLOAT;
      case "string":
        return STRING;
      case "char":
        return CHAR;
      case "bool":
        return BOOL;
      case "list":
        return this.checkListLit(expr, env, expected);
      case "brace":
        return this.checkBraceLit(expr, env, expected);
      case "ident": {
        const def = env.lookup(expr.name);
        if (def) {
          this.recordRef(expr.span, def, def.type);
          return def.type;
        }
        const global = this.globalDefs.get(expr.name);
        if (global) {
          this.recordRef(expr.span, global, global.type);
          // A bare reference to a (non-generic) top-level function is a
          // first-class function value; its type is the function's signature.
          const fn = this.fns.get(expr.name);
          if (fn && fn.typeParams.length === 0) return this.fnValueType(fn);
          return UNKNOWN; // a type/constructor/generic fn used as a callee elsewhere
        }
        this.error(`undefined variable \`${expr.name}\``, expr.span);
        return UNKNOWN;
      }
      case "unary": {
        const t = this.checkExpr(expr.operand, env);
        if (expr.op === "-") this.expectNumeric(t, expr.span);
        return expr.op === "-" ? t : BOOL;
      }
      case "binary":
        return this.checkBinary(expr, env);
      case "field":
        return this.checkField(expr, env);
      case "call":
        return this.checkCall(expr, env, expected);
      case "lambda":
        return this.checkLambda(expr, env, expected);
      case "ifExpr":
        return this.checkIfExpr(expr, env, expected);
    }
  }

  /** An if-*expression* `if cond { a } else { b }`: the condition must be `bool`, and
   *  the whole expression's type is the two branches' — which must agree, with an
   *  `int` widening to `float` the way a list literal's elements do (`impliedType`).
   *  Each branch is checked against `expected` (the context, e.g. a `let`
   *  annotation), the else falling back to the then branch's type so the two infer
   *  together — the same trick that lets an `else None()` resolve from the `then`. */
  private checkIfExpr(expr: ast.IfExpr, env: Env, expected?: Type): Type {
    this.expectBool(this.checkExpr(expr.cond, env), expr.cond.span);
    const thenT = this.checkExpr(expr.then, env, expected);
    const elseT = this.checkExpr(expr.else, env, expected ?? thenT);
    if (
      assignable(thenT, elseT, this.implementsTraitType) ||
      assignable(elseT, thenT, this.implementsTraitType)
    ) {
      return this.impliedType([thenT, elseT]) ?? thenT;
    }
    this.error(
      `\`if\`/\`else\` branches have differing types (${typeStr(thenT)} and ${typeStr(elseT)}) — both must yield the same type`,
      expr.span
    );
    return thenT;
  }

  /** Check a call argument, forwarding the `expected` parameter type in for local
   *  bidirectional inference: a lambda infers its un-annotated parameters from it,
   *  and an argumentless `None()` resolves its type argument from it. Routes through
   *  `checkExpr` (including for lambdas, whose case threads `expected`) so the
   *  argument's type is recorded in the elaboration like any other expression. */
  private checkArg(value: ast.Expr, env: Env, expected: Type | undefined): Type {
    return this.checkExpr(value, env, expected);
  }

  /** A lambda's type is the function type `(paramTypes) -> bodyType`. Parameters
   *  are declared in a fresh child scope nested in `env`, so the body may read
   *  the enclosing scope's bindings — that lexical capture is what makes it a
   *  closure. The return type is the expression body's type, or — for a block
   *  body — the join of its `return` statements' types (`void` if it never
   *  returns a value).
   *
   *  An un-annotated parameter's type comes from `expected` when the lambda is
   *  an argument whose parameter type is known (e.g. the `f: (T) -> U` a `.map`
   *  expects), so the body is checked against a concrete element type instead of
   *  `unknown`. Only fully-resolved expected parameter types are pushed in: an
   *  accumulator slot still awaiting inference (a bare type variable, as in
   *  `reduce`'s `A`) is left as `unknown` for later, and a parameter with no
   *  expected type is `unknown` (best-effort). */
  private checkLambda(expr: ast.LambdaExpr, env: Env, expected?: Type): Type {
    const inner = this.childScope(env, expr.span);
    const expectedParams = expected?.k === "fn" ? expected.params : undefined;
    const params = expr.params.map((p, i) => {
      const fromExpected = expectedParams?.[i];
      const t = p.type
        ? this.resolveType(p.type)
        : fromExpected && !hasTypeVar(fromExpected) && fromExpected.k !== "unknown"
          ? fromExpected
          : UNKNOWN;
      inner.declare({ name: p.name, kind: "param", nameSpan: p.nameSpan, type: t, detail: `${p.name}: ${typeStr(t)}` });
      return t;
    });
    // When the lambda is checked against a known function type, its return type is
    // the expectation for the body — so an under-determined body like `None()`
    // resolves from it (the `orElse` cross-branch case, once the other branch has
    // pinned the type).
    const expectedRet = expected?.k === "fn" ? expected.ret : undefined;
    if (expr.body.kind === "block") {
      // Collect the block's `return` types via a sink (see `checkStmt`'s return
      // case), so the lambda's result type is inferred rather than declared.
      const sink = { types: [] as Type[], expected: expectedRet };
      this.returnSinks.push(sink);
      // A lambda's *body* runs wherever the value is eventually called, not where it
      // is written — so an enclosing `try` does not discharge a `raise` inside it.
      // (Its effect is still attributed to the enclosing callable, exactly as `io`
      // inside a lambda is.)
      const savedHandlers = this.handlers;
      this.handlers = [];
      try {
        this.checkBlock(expr.body, inner, UNKNOWN);
      } finally {
        this.returnSinks.pop();
        this.handlers = savedHandlers;
      }
      return { k: "fn", params, ret: joinReturnTypes(sink.types) };
    }
    const ret = this.checkExpr(expr.body, inner, expectedRet);
    return { k: "fn", params, ret };
  }

  /** A list literal `[a, b, c]` — a `List<T>` whose `T` is never written, so it comes
   *  from one of two places. When the context supplies an expected `List<…>` (a `let`
   *  annotation, a parameter, a field default, a return type) that fixes `T` and each
   *  element is checked against it. Otherwise `T` is inferred from the elements: the
   *  first element's type, and every later element must be assignable to it.
   *
   *  Two wrinkles the rule has to state outright. `int` and `float` are mutually
   *  assignable, so first-element-wins alone would quietly type `[1, 2.5]` as
   *  `List<int>`; a float anywhere widens the element type. And there is deliberately
   *  no least-upper-bound search — `[circle, rect]` is an error rather than a
   *  `List<Shape>`, because the checker has no join anywhere else and the annotation
   *  that says what was meant is read by the branch above. */
  private checkListLit(expr: ast.ListLit, env: Env, expected?: Type): Type {
    const fromContext = this.literalTypeArg(expected, "List", 0);
    // Each element is checked against the expected element type when there is one,
    // and against the first element's type otherwise — so an under-determined later
    // element (`[Some(1), None()]`) still resolves from its neighbours.
    const types: Type[] = [];
    expr.elements.forEach((e, i) => {
      types.push(this.checkExpr(e, env, fromContext ?? (i > 0 ? types[0] : undefined)));
    });
    let elem = fromContext ?? this.impliedType(types);
    if (!elem) {
      // Nothing written, nothing to infer from — and, like a bare `Set()`, an error
      // rather than a lingering `List<unknown>`: the constructor already spells an
      // empty list of a named type, so there is nothing new to reach for.
      this.error(
        "cannot infer the element type of an empty list — write `List<T>()` with its element type, or annotate the target",
        expr.span
      );
      elem = UNKNOWN;
    }
    const where = { container: "list", role: "element", fromContext: fromContext !== undefined } as const;
    expr.elements.forEach((e, i) => this.checkLiteralPart(e, types[i]!, elem, where));
    return { k: "generic", name: "List", args: [elem] };
  }

  /** A brace literal — a `Set<T>` (`{a, b}`) or a `Dict<K, V>` (`{k: v}`). Which one
   *  is a *parse* fact for a non-empty literal (does the first element carry a `:`),
   *  so this only has to settle the empty case before handing off. */
  private checkBraceLit(expr: ast.BraceLit, env: Env, expected?: Type): Type {
    // `{}` is neither shape on its own — only the context can say which container it
    // is (and, either way, what it holds). Without one there is nothing to guess
    // from, so it is an error rather than a defaulted-to-dict, which is the arbitrary
    // choice Python is stuck with.
    const container =
      expr.container ??
      (expected?.k === "generic" && (expected.name === "Set" || expected.name === "Dict")
        ? expected.name
        : null);
    if (!container) {
      this.error(
        "cannot tell whether `{}` is a set or a dict — write `Set<T>()` or `Dict<K, V>()`, or annotate the target",
        expr.span
      );
      return UNKNOWN;
    }
    return container === "Dict" ? this.checkDictLit(expr, env, expected) : this.checkSetLit(expr, env, expected);
  }

  /** A set literal `{a, b, …}` — a `Set<T>`, typed by the same rule a list literal's
   *  elements follow. Duplicate elements are not an error: they are added in order,
   *  so the second collapses into the first, exactly as `.add` would. */
  private checkSetLit(expr: ast.BraceLit, env: Env, expected?: Type): Type {
    const fromContext = this.literalTypeArg(expected, "Set", 0);
    const types: Type[] = [];
    expr.entries.forEach((entry, i) => {
      types.push(this.checkExpr(entry.value, env, fromContext ?? (i > 0 ? types[0] : undefined)));
    });
    let elem = fromContext ?? this.impliedType(types);
    if (!elem) {
      this.error(
        "cannot infer the element type of an empty set — write `Set<T>()` with its element type, or annotate the target",
        expr.span
      );
      elem = UNKNOWN;
    }
    const where = { container: "set", role: "element", fromContext: fromContext !== undefined } as const;
    expr.entries.forEach((entry, i) => this.checkLiteralPart(entry.value, types[i]!, elem, where));
    return { k: "generic", name: "Set", args: [elem] };
  }

  /** A dict literal `{k: v, …}` — a `Dict<K, V>` whose types are never written. The
   *  key and value types are inferred *independently*, each by the rule a list
   *  literal's elements follow: the context's if it fixes one, else the first entry's
   *  with float widening, and no least-upper-bound search.
   *
   *  Duplicate keys are not an error: the entries are written in order, so a later one
   *  overwrites an earlier one, exactly as the `.set` sequence this replaces would. */
  private checkDictLit(expr: ast.BraceLit, env: Env, expected?: Type): Type {
    const keyContext = this.literalTypeArg(expected, "Dict", 0);
    const valueContext = this.literalTypeArg(expected, "Dict", 1);
    const keys: Type[] = [];
    const values: Type[] = [];
    expr.entries.forEach((entry, i) => {
      // Every entry of a dict literal carries a key (the parser rejects a mixed one).
      keys.push(this.checkExpr(entry.key!, env, keyContext ?? (i > 0 ? keys[0] : undefined)));
      values.push(this.checkExpr(entry.value, env, valueContext ?? (i > 0 ? values[0] : undefined)));
    });
    let key = keyContext ?? this.impliedType(keys);
    let value = valueContext ?? this.impliedType(values);
    if (!key || !value) {
      this.error(
        "cannot infer the types of an empty dict — write `Dict<K, V>()` with its key and value types, or annotate the target",
        expr.span
      );
      key = key ?? UNKNOWN;
      value = value ?? UNKNOWN;
    }
    const keyWhere = { container: "dict", role: "key", fromContext: keyContext !== undefined } as const;
    const valueWhere = { container: "dict", role: "value", fromContext: valueContext !== undefined } as const;
    expr.entries.forEach((entry, i) => {
      this.checkLiteralPart(entry.key!, keys[i]!, key, keyWhere);
      this.checkLiteralPart(entry.value, values[i]!, value, valueWhere);
    });
    return { k: "generic", name: "Dict", args: [key, value] };
  }

  /** The type argument an expected container type fixes for a literal: `expected`'s
   *  `i`th argument, when it is an instance of `container` and that argument is
   *  known. This is what lets the context type a literal instead of its contents —
   *  a `let` annotation, a parameter, a field's declared type, a return type. */
  private literalTypeArg(expected: Type | undefined, container: string, i: number): Type | undefined {
    if (expected?.k !== "generic" || expected.name !== container) return undefined;
    const arg = expected.args[i];
    return arg && arg.k !== "unknown" ? arg : undefined;
  }

  /** The type a literal's like-positioned expressions imply — the first one's, except
   *  that a `float` among numerics widens it. `assignable` treats int and float as
   *  interchangeable, so first-wins alone would type `[1, 2.5]` as `List<int>`.
   *  Undefined when there is nothing to infer from; each literal reports that its
   *  own way, since what an empty one is missing differs per container. */
  private impliedType(types: Type[]): Type | undefined {
    const first = types[0];
    if (!first) return undefined;
    if (first.k === "int" && types.some((t) => t.k === "float")) return FLOAT;
    return first;
  }

  /** Report a literal part that doesn't fit the type the literal settled on. The
   *  message turns on where that type came from: an expectation the context imposed
   *  (so this part is simply the wrong type), or the parts' own types disagreeing (so
   *  the fix is to say which was meant). */
  private checkLiteralPart(
    part: ast.Expr,
    got: Type,
    want: Type,
    where: { container: "list" | "set" | "dict"; role: "element" | "key" | "value"; fromContext: boolean }
  ): void {
    if (assignable(want, got, this.implementsTraitType)) return;
    const { container, role, fromContext } = where;
    this.error(
      fromContext
        ? `${container} ${role} expects ${typeStr(want)}, got ${typeStr(got)}`
        : `${container} ${role}s have differing types (${typeStr(want)} and ${typeStr(got)}) — annotate the ${container} to fix its ${role} type`,
      part.span
    );
  }

  private checkBinary(expr: ast.BinaryExpr, env: Env): Type {
    const l = this.checkExpr(expr.left, env);
    const r = this.checkExpr(expr.right, env);
    // Record how the operator is realized (native vs. a trait-method overload) for
    // backends to lower without re-deriving it. Overloads dispatch on the left
    // operand's type, and only user types (struct/enum) can carry them.
    const overload = (trait: string, method: string) =>
      this.elab.binaries.set(expr, { kind: "overload", trait, method, receiver: l });
    const native = () => this.elab.binaries.set(expr, { kind: "native" });
    switch (expr.op) {
      case "&&":
      case "||":
        this.expectBool(l, expr.left.span);
        this.expectBool(r, expr.right.span);
        native();
        return BOOL;
      case "==":
      case "!=": {
        // `==` resolves through `Eq`, for every type that implements it and not
        // just a user one: the scalars and the containers implement it in the
        // prelude (with `builtin` bodies — see `impl Eq for int` in core.stm), so
        // which types may be compared is *declared* rather than hardcoded here.
        // The operand is checked against the impl's `Self`, which is what catches
        // `opt == 123` on an `Option<int>` — the shape where the intent was
        // `opt == Some(123)` (or a `match` / `getOr`).
        const eq = this.satisfiesBound(l, "Eq")
          ? this.members(l).find((m) => m.name === "eq" && m.params)
          : undefined;
        if (eq && !eq.builtin) {
          // A user-written `eq`: the operator dispatches to it, and a bad operand
          // is reported against the `Self` parameter the impl declares.
          this.checkOperandAgainstSelf("eq", l, r, expr);
          overload("Eq", "eq");
        } else {
          // Either a `builtin` impl or no impl at all. A `builtin` one *is* the
          // native operator, and a type without one keeps the documented default
          // (structural for value types and enums, identity for heap structs), so
          // both lower natively — there is no method to call. Either way the
          // operand is checked against `Self`, which for `Eq.eq(self, other: Self)`
          // is the left type. Taken as `l` directly: `Self` off the impl now
          // substitutes to the same thing, but the default branch has no impl to
          // read it from, so one source covers both.
          if (!comparable(l, r, this.implementsTraitType))
            this.error(
              `cannot compare \`${typeStr(l)}\` with \`${typeStr(r)}\` using \`${expr.op}\``,
              expr.span
            );
          native();
        }
        return BOOL;
      }
      case "<":
      case ">":
      case "<=":
      case ">=":
        // A user type must implement `Ord` to be ordered; scalars compare directly.
        if (l.k === "struct" || l.k === "enum") {
          if (!this.implementsTrait(l.name, "Ord")) {
            this.error(`\`${typeStr(l)}\` does not implement \`Ord\`, so \`${expr.op}\` is not defined on it`, expr.span);
            native();
          } else {
            this.checkOperandAgainstSelf("cmp", l, r, expr);
            overload("Ord", "cmp");
          }
        } else if (l.k === "var" && l.bounds.includes("Ord")) {
          // A type parameter bound by `Ord` orders through `Ord.cmp` — recorded as
          // an overload so a backend that dispatches on the concrete value (the
          // interpreter) calls the right `cmp`. Dispatch is by the runtime value, so
          // a scalar instantiation (`min(1, 2)`) still uses the native comparison —
          // the interpreter falls back to it when the operand isn't a user type, and
          // monomorphization re-derives the operator from each concrete operand type.
          // This is what makes `fn min<T: Ord>(a: T, b: T) T { if a < b { … } }` work
          // for both a scalar and a user `Ord` type.
          overload("Ord", "cmp");
        } else {
          // Native ordering is defined on the ordered scalars — numbers (`int`
          // and `float`, which order against each other), `char`, `string`
          // (lexicographic), and `bool` (`false < true`) — plus a type parameter
          // / trait value that resolves to one (`unknown` stays lenient after an
          // earlier error). Both operands must share an ordering domain; anything
          // else — a container (`Dict`, `Set`, a `Table`) — has no ordering,
          // unlike `==`/`!=`, which stay structural for every pair.
          const domain = (t: Type): string | null =>
            t.k === "unknown" || t.k === "var" || t.k === "trait" ? "*"
            : numeric(t) ? "num"
            : t.k === "char" || t.k === "string" || t.k === "bool" ? t.k
            : null;
          const dl = domain(l);
          const dr = domain(r);
          if (dl === null || dr === null) {
            this.error(`\`${typeStr(dl === null ? l : r)}\` is not ordered, so \`${expr.op}\` is not defined on it`, expr.span);
          } else if (dl !== "*" && dr !== "*" && dl !== dr) {
            // Both are ordered but in different domains (e.g. a `char` and an
            // `int`) — comparing across kinds has no meaning.
            this.error(`cannot compare \`${typeStr(l)}\` with \`${typeStr(r)}\` using \`${expr.op}\``, expr.span);
          }
          native();
        }
        return BOOL;
      case "+":
      case "-":
      case "*":
      case "/":
      case "%": {
        // `+` also concatenates strings.
        if (expr.op === "+" && l.k === "string" && r.k === "string") {
          native();
          return STRING;
        }
        // Operator overloading: `a + b` on a user type dispatches to its `Add`
        // (etc.) impl. Only the left operand's type selects the operator.
        const over = this.overloadedBinary(expr.op, l, r, expr);
        if (over) {
          const entry = OPERATOR_TRAITS[expr.op]!;
          this.elab.binaries.set(expr, { kind: "overload", trait: entry.trait, method: entry.method, receiver: l });
          return over;
        }
        // A type parameter bound by the operator's trait (`T: Add`) computes
        // through that trait's method — the arithmetic mirror of the `Ord` case
        // above, and what makes `fn sum<T: Add>(items: Iterable<T>)` work for both
        // a user `Add` type and a scalar: dispatch is by the runtime value (the
        // interpreter falls back to native arithmetic when the operand isn't a user
        // type), and monomorphization re-derives the operator from the concrete
        // operand type.
        const bounded = OPERATOR_TRAITS[expr.op];
        if (bounded && l.k === "var" && l.bounds.includes(bounded.trait)) {
          this.elab.binaries.set(expr, {
            kind: "overload",
            trait: bounded.trait,
            method: bounded.method,
            receiver: l,
          });
          return l;
        }
        this.expectNumeric(l, expr.left.span);
        this.expectNumeric(r, expr.right.span);
        native();
        return l.k === "float" || r.k === "float" ? FLOAT : INT;
      }
    }
  }

  /** If `op` is an overloadable arithmetic operator and the left operand is a
   *  user type (struct/value-type/enum), resolve it through the operator's trait
   *  (`+` -> `Add.add`, …): check the right operand against the method's `Self`
   *  parameter and return its result type. Returns null when the operator isn't
   *  overloaded on this operand (so numeric checking proceeds). */
  private overloadedBinary(op: string, l: Type, r: Type, expr: ast.BinaryExpr): Type | null {
    const entry = OPERATOR_TRAITS[op];
    if (!entry || (l.k !== "struct" && l.k !== "enum")) return null;
    const member = this.members(l).find((m) => m.name === entry.method && m.params);
    if (!member || !this.implementsTrait(l.name, entry.trait)) {
      this.error(
        `\`${typeStr(l)}\` does not implement \`${entry.trait}\`, so \`${op}\` is not defined on it`,
        expr.span
      );
      return UNKNOWN;
    }
    const otherT = member.params?.[0]?.type ?? UNKNOWN;
    if (!assignable(otherT, r, this.implementsTraitType)) {
      this.error(
        `operator \`${op}\` on ${typeStr(l)} expects ${typeStr(otherT)}, got ${typeStr(r)}`,
        expr.right.span
      );
    }
    return member.ret ? member.ret([r]) : member.type;
  }

  /** Check the right operand of a comparison against the `Self` parameter of the
   *  left type's `Eq.eq` / `Ord.cmp` method (both take `other: Self`). */
  private checkOperandAgainstSelf(method: string, l: Type, r: Type, expr: ast.BinaryExpr): void {
    const member = this.members(l).find((m) => m.name === method && m.params);
    const otherT = member?.params?.[0]?.type ?? UNKNOWN;
    if (!assignable(otherT, r, this.implementsTraitType)) {
      this.error(
        `operator \`${expr.op}\` on ${typeStr(l)} expects ${typeStr(otherT)}, got ${typeStr(r)}`,
        expr.right.span
      );
    }
  }

  /** The members of `type`, resolved against this program's struct fields and
   *  `impl`/trait methods. For a generic struct/enum instantiation, the type's
   *  parameters are substituted with the instance's concrete arguments — so
   *  `p.first` on a `Pair<int, string>` is typed `int`, and a method's `T`-typed
   *  parameter or return (from `impl<T> ... for Box<T>`) reads as the concrete
   *  element type. */
  private members(type: Type): Member[] {
    return typeMembers(
      type,
      (n) => this.structFields.get(n) ?? [],
      (n) => this.typeMethods.get(n) ?? [],
      (t) => this.declaredTypeParams(t)
    );
  }

  /** A generic type's declared parameter names, to specialize its members with an
   *  instance's arguments: a struct/enum/trait's own params, or a container's
   *  (its inherent impl's — so `Dict.get`'s `Option<V>` and `Set<T>`'s
   *  `map`/`filter`/`reduce` read their `V`/`T` as the concrete element). */
  private declaredTypeParams(type: Type): string[] | undefined {
    return (
      type.k === "struct-generic" ? this.structs.get(type.name)?.typeParams.map((p) => p.name)
      : type.k === "enum-generic" ? this.enums.get(type.name)?.typeParams.map((p) => p.name)
      : type.k === "trait" ? this.traits.get(type.name)?.typeParams.map((p) => p.name)
      : type.k === "generic" ? containerTypeParams(type.name)
      : undefined
    );
  }

  private checkField(expr: ast.FieldAccess, env: Env): Type {
    const obj = this.checkExpr(expr.object, env);
    const member = this.members(obj).find((m) => m.name === expr.field);
    if (member) {
      // A method named but not called resolves during call handling.
      if (member.params) return UNKNOWN;
      this.recordRef(expr.fieldSpan, member, member.type);
      return member.type;
    }
    // No such member. Diagnose only for the types whose members we fully know.
    if (obj.k === "struct" || obj.k === "struct-generic")
      this.error(`\`${obj.name}\` has no field \`${expr.field}\``, expr.span);
    return UNKNOWN;
  }

  /** Check a call to a callable member. Handles container type-parameter
   *  inference (an untyped `Set`/`Dict` learning its args from the first call),
   *  checks arguments, and computes the return type from the actual arguments. */
  private checkMemberCall(recv: Type, member: Member, expr: ast.CallExpr, argTypes: Type[]): Type {
    // A generic method (`map<U>(...)`) infers its type parameters from the args.
    if (member.typeParams && member.typeParams.length > 0) {
      return this.checkGenericMemberCall(recv, member, expr, argTypes);
    }
    const params = member.params ?? [];
    // A user-defined method's arity is fixed (containers stay lenient).
    if (member.kind === "method" && argTypes.length !== params.length) {
      this.error(`\`${member.name}\` expects ${params.length} argument(s), got ${argTypes.length}`, expr.span);
    }
    this.recordRef((expr.callee as ast.FieldAccess).fieldSpan, member, member.type);
    params.forEach((p, i) => {
      const expected = p.type;
      const got = argTypes[i];
      if (expected.k !== "unknown" && got && !assignable(expected, got, this.implementsTraitType)) {
        const role = p.role ? `${p.role} ` : "";
        this.error(
          `\`${member.name}\` on ${typeStr(recv)} expects ${role}${typeStr(expected)}, got ${typeStr(got)}`,
          expr.args[i]!.value.span
        );
      }
    });
    return member.ret ? member.ret(argTypes) : member.type;
  }

  /** Check a call to a generic method: infer its type parameters by unifying each
   *  (type-variable-bearing) parameter type against the argument, verify each
   *  inferred type satisfies the parameter's bounds, and return the method's
   *  return type with the parameters substituted away. Mirrors `checkGenericCall`
   *  for free functions. */
  private checkGenericMemberCall(recv: Type, member: Member, expr: ast.CallExpr, argTypes: Type[]): Type {
    const params = member.params ?? [];
    const rawRet = member.ret ? member.ret(argTypes) : member.type;
    this.recordRef((expr.callee as ast.FieldAccess).fieldSpan, member, member.type);
    if (argTypes.length !== params.length) {
      this.error(`\`${member.name}\` expects ${params.length} argument(s), got ${argTypes.length}`, expr.span);
      return rawRet;
    }
    const subst = new Map<string, Type>();
    const conflicts = new Set<string>();
    params.forEach((p, i) => {
      if (!this.unify(p.type, argTypes[i]!, subst, conflicts)) {
        this.error(
          `\`${member.name}\` on ${typeStr(recv)} expects ${typeStr(p.type)}, got ${typeStr(argTypes[i]!)}`,
          expr.args[i]!.value.span
        );
      }
    });
    for (const c of conflicts) this.error(`type parameter ${c} in call to \`${member.name}\``, expr.span);
    for (const tp of member.typeParams!) {
      const concrete = subst.get(tp.name);
      if (!concrete || concrete.k === "unknown" || concrete.k === "var") continue;
      for (const b of tp.bounds) {
        if (!this.satisfiesBound(concrete, b.name)) {
          this.error(
            `type \`${typeStr(concrete)}\` does not implement \`${b.name}\` (required by \`${tp.name}\` in \`${member.name}\`)`,
            expr.span
          );
        }
      }
    }
    // Record the instantiation so a backend reads it instead of re-inferring.
    this.elab.typeArgs.set(expr, member.typeParams!.map((tp) => subst.get(tp.name) ?? UNKNOWN));
    return this.substitute(rawRet, subst);
  }

  /** The expected type for each positional argument of a call, when the callee's
   *  parameter types are known and concrete: a function-typed binding, or a
   *  non-generic free function. This drives local bidirectional inference for the
   *  arguments — an argumentless `None()` passed where `Option<int>` is expected
   *  resolves its type argument. Returns `undefined` per position when there is no
   *  useful expectation (a generic callee whose parameters bear type variables, a
   *  constructor, a builtin, or an arity mismatch), leaving that argument checked on
   *  its own — matching the previous behavior for those callees. */
  private argExpectations(
    name: string,
    local: DefInfo | undefined,
    expr: ast.CallExpr,
    expected: Type | undefined
  ): (Type | undefined)[] {
    // A function-typed value (local binding or parameter): its parameter types are
    // concrete, and it shadows any same-named global — so it is checked first.
    if (local && local.type.k === "fn") {
      const params = local.type.params;
      return expr.args.map((_, i) => params[i]);
    }
    // A generic struct/variant constructor whose instance the context fixes: thread
    // each field's expected type — the expected instance's arguments substituted into
    // the declared field types — into the field arguments, so a nested payload-free
    // variant (`Ok(ok: None())` where `Res<…, Option<string>>` is expected) resolves
    // its `None()` from context instead of recording `Option<unknown>`.
    const ctor = this.constructorArgExpectations(name, expr, expected);
    if (ctor) return ctor;
    // A non-generic free function: resolve each declared parameter type. Constructors
    // (same-named struct/variant) take precedence over a free function, and a generic
    // function's parameters bear type variables that aren't useful here — both are
    // left without an expectation.
    const fn = this.fns.get(name);
    if (
      fn &&
      fn.typeParams.length === 0 &&
      !this.structs.has(name) &&
      !this.variantOf.has(name) &&
      expr.args.length === fn.params.length
    )
      return fn.params.map((p) => this.resolveType(p.type));
    return expr.args.map(() => undefined);
  }

  /** Per-argument expected types for a struct/variant constructor: each argument is
   *  checked against the field it fills. A **non-generic** constructor's field types
   *  are already concrete, so this needs no context — it is what types an
   *  under-determined argument like `C(items: [])` or `C(f: (x) -> x + 1)`, including
   *  the clone `fillFieldDefaults` appends for an omitted defaulted field (which is
   *  checked here, at the construction, not at the declaration). A **generic** one's
   *  field types bear its parameters, so an expectation exists only when the
   *  surrounding context fixes the instance (`expected`): map the constructed type's
   *  parameters to that instance's arguments and substitute them into the field types,
   *  so a nested payload-free variant (`Ok(ok: None())` where `Res<…, Option<string>>`
   *  is expected) resolves from context instead of recording `Option<unknown>`.
   *  Returns `null` when the callee isn't a constructor, or is a generic one whose
   *  instance the context doesn't fix. */
  private constructorArgExpectations(name: string, expr: ast.CallExpr, expected: Type | undefined): (Type | undefined)[] | null {
    let typeParams: ast.TypeParam[];
    let fieldDecls: ast.FieldDecl[];
    // The expected instance's type arguments, when the context fixes one.
    let instanceArgs: Type[] | undefined;
    if (this.structs.has(name)) {
      const d = this.structs.get(name)!;
      typeParams = d.typeParams;
      fieldDecls = d.fields;
      if (expected?.k === "struct-generic" && expected.name === name) instanceArgs = expected.args;
    } else if (this.variantOf.has(name)) {
      const enumName = this.variantOf.get(name)!;
      typeParams = this.enums.get(enumName)!.typeParams;
      fieldDecls = this.variantDecl.get(name)!.fields;
      if (expected?.k === "enum-generic" && expected.name === enumName) instanceArgs = expected.args;
    } else return null;
    const subst = new Map<string, Type>();
    if (typeParams.length > 0) {
      if (!instanceArgs || typeParams.length !== instanceArgs.length) return null;
      typeParams.forEach((p, i) => subst.set(p.name, instanceArgs![i]!));
    }
    // Resolve field types with the constructed type's parameters in scope, then
    // substitute the expected instance's arguments for them.
    const fieldTypes = this.withTypeVars(typeParams, () =>
      fieldDecls.map((f) => ({ name: f.name, type: this.substitute(this.resolveType(f.type), subst) }))
    );
    return expr.args.map((arg, i) => {
      const field = arg.name ? fieldTypes.find((f) => f.name === arg.name) : fieldTypes[i];
      return field?.type;
    });
  }

  /** Bind a call's arguments to `params` in declaration order: positional arguments
   *  by position, named ones (`f(b: 2, a: 1)`) by name. An omitted parameter that has
   *  a default is filled with a *deep clone* of that default expression — a fresh node
   *  per call, so it is checked and lowered independently (a `List` default becomes a
   *  distinct list at every call). The array is rewritten in place so downstream
   *  checking and every backend see complete positional arguments — the same treatment
   *  a constructor's fields get. Reports unknown/duplicate/too-many/missing arguments;
   *  on any error the arguments are left untouched. A fully-positional call with
   *  nothing to fill (the common case) is left untouched for downstream arity
   *  checking, which knows about variadic builtins. */
  private bindCallArgs(expr: ast.CallExpr, params: { name: string; default?: ast.Expr }[]): void {
    const hasNamed = expr.args.some((a) => a.name);
    // A fully-positional call whose length matches (or exceeds — e.g. a variadic
    // builtin) is left untouched: there is nothing to reorder, no default to fill,
    // and any genuine arity error is reported downstream where variadics are known.
    // Only reorder/validate when named arguments are present, or when a shortfall
    // could be covered by a defaulted parameter.
    const canFill = expr.args.length < params.length && params.some((p) => p.default);
    if (!hasNamed && !canFill) return;
    const ordered: (ast.CallArg | undefined)[] = new Array(params.length).fill(undefined);
    let ok = true;
    let pos = 0;
    for (const arg of expr.args) {
      const idx = arg.name ? params.findIndex((p) => p.name === arg.name) : pos++;
      if (idx < 0 || idx >= params.length) {
        this.error(arg.name ? `no parameter named \`${arg.name}\`` : `too many arguments`, arg.nameSpan ?? arg.value.span);
        ok = false;
        continue;
      }
      if (ordered[idx]) {
        this.error(`argument \`${params[idx]!.name}\` given more than once`, arg.nameSpan ?? arg.value.span);
        ok = false;
        continue;
      }
      ordered[idx] = arg;
    }
    // Fill each unbound parameter from its default (cloned per call), or report it
    // missing when it has none.
    params.forEach((p, i) => {
      if (ordered[i]) return;
      if (p.default) ordered[i] = { name: null, value: cloneNode(p.default) };
      else {
        this.error(`missing argument \`${p.name}\``, expr.callee.span);
        ok = false;
      }
    });
    if (ok) expr.args = ordered as ast.CallArg[];
  }

  /** The parameters of the callable a bare-identifier call names — a non-constructor
   *  free function (with its defaults) or a builtin — for binding named/defaulted
   *  arguments. `null` when the callee has no named parameters to match against (a
   *  function-typed value, whose type carries no names, or a constructor, whose fields
   *  self-match by name). */
  private callableParams(name: string, local: DefInfo | undefined): { name: string; default?: ast.Expr }[] | null {
    if (local && local.type.k === "fn") return null;
    if (this.structs.has(name) || this.variantOf.has(name)) return null;
    const fn = this.fns.get(name);
    if (fn) return fn.params.map((p) => ({ name: p.name, default: p.default }));
    const builtin = this.builtinDecls.get(name);
    if (builtin) return builtin.params.map((p) => ({ name: p.name }));
    return null;
  }

  private checkCall(expr: ast.CallExpr, env: Env, expected?: Type): Type {
    // method call: resolve `method` among the receiver type's members.
    if (expr.callee.kind === "field") {
      const recv = this.checkExpr(expr.callee.object, env);
      const m = expr.callee.field;
      const member = this.members(recv).find((mem) => mem.name === m && mem.params);
      // Named arguments bind to the method's parameters by name; reorder so the rest
      // of checking (and the backends) see them positionally.
      if (member?.params) this.bindCallArgs(expr, member.params.map((p) => ({ name: p.label, default: p.default })));
      // Push the resolved parameter types into any lambda argument so its
      // body is checked against them — e.g. `dict.map((p) -> …)` types `p` as
      // the dict's `Pair<K, V>` element rather than leaving it `unknown`.
      const argTypes = expr.args.map((a, i) => this.checkArg(a.value, env, member?.params?.[i]?.type));
      if (member) {
        // A trait-typed receiver dispatches dynamically at runtime; a concrete one
        // (struct / value type / enum / container) is a direct call.
        this.elab.calls.set(expr, { kind: "method", method: m, receiver: recv, dynamic: recv.k === "trait" });
        this.noteCall(this.methodTargets(recv, m).map((key) => ({ key, name: m, span: expr.span })));
        return this.checkMemberCall(recv, member, expr, argTypes);
      }
      // Not a method — a struct field holding a function value is callable too.
      const field = this.members(recv).find((mem) => mem.name === m && !mem.params);
      if (field) {
        if (field.type.k === "fn") {
          this.recordRef(expr.callee.fieldSpan, field, field.type);
          this.elab.calls.set(expr, { kind: "fn-value", name: m });
          return this.checkFnValueCall(m, field.type, expr, argTypes);
        }
        if (field.type.k !== "unknown") {
          this.error(`\`${m}\` is a ${typeStr(field.type)} field of \`${typeStr(recv)}\`, not a function`, expr.span);
          return UNKNOWN;
        }
      }
      // Unknown method: diagnose only when the receiver's member set is known.
      if (recv.k === "generic") this.error(`unknown ${recv.name} method \`${m}\``, expr.span);
      else if (recv.k === "struct" || recv.k === "struct-generic" || recv.k === "enum" || recv.k === "enum-generic" || recv.k === "trait" || recv.k === "string" || recv.k === "char")
        this.error(`\`${typeStr(recv)}\` has no method \`${m}\``, expr.span);
      else if (recv.k === "var")
        this.error(
          `\`${recv.name}\` has no method \`${m}\`${recv.bounds.length ? ` (its bounds are ${recv.bounds.join(", ")})` : " (it has no trait bounds)"}`,
          expr.span
        );
      return UNKNOWN;
    }

    if (expr.callee.kind !== "ident") {
      // Any other callee expression (e.g. an immediately-invoked lambda
      // `((x) -> x)(5)`) — call it if it has a function type, checking arguments
      // against its (concrete) parameter types.
      const calleeType = this.checkExpr(expr.callee, env);
      const params = calleeType.k === "fn" ? calleeType.params : [];
      const argTypes = expr.args.map((a, i) => this.checkArg(a.value, env, params[i]));
      if (calleeType.k === "fn") {
        this.elab.calls.set(expr, { kind: "fn-value", name: "function" });
        return this.checkFnValueCall("function", calleeType, expr, argTypes);
      }
      return UNKNOWN;
    }
    const name = expr.callee.name;
    // Check arguments against the callee's parameter types where those are known and
    // concrete (a non-generic free function, or a function-typed value), so an
    // under-determined argument like `None()` resolves from the parameter it fills
    // rather than collapsing to `unknown`; a generic constructor whose instance the
    // context fixes threads each field's expected type in the same way. Other callees
    // (generic functions, builtins) get no per-argument expectation.
    const local = env.lookup(name);
    // Named arguments (`f(b: 2, a: 1)`) bind to parameters by name; reorder into
    // declaration order so the rest of checking — and every backend — sees them
    // positionally. (A constructor's fields are matched by name separately.)
    const params = this.callableParams(name, local);
    if (params) this.bindCallArgs(expr, params);
    // A constructor's fields self-match by name; fill any omitted field that
    // declares a default with a fresh clone of it, appended as a named argument,
    // before argument types are computed so it is checked like any other field.
    if (this.structs.has(name)) this.fillFieldDefaults(expr, this.structs.get(name)!.fields);
    else if (this.variantOf.has(name)) this.fillFieldDefaults(expr, this.variantDecl.get(name)!.fields);
    // A generic constructor's field default may reference the type's parameters
    // (`items List<T> = List<T>()`). Bind them while the arguments — including those
    // filled defaults — are checked: to the explicit type arguments when given
    // (`Chan<int>()` → T=int), otherwise to each parameter's own type variable so the
    // default still type-checks and the instance is inferred/monomorphized as usual.
    const ctorTypeParams = this.structs.has(name)
      ? this.structs.get(name)!.typeParams
      : this.variantOf.has(name)
        ? this.enums.get(this.variantOf.get(name)!)!.typeParams
        : [];
    const savedTypeVars = this.typeVars;
    if (ctorTypeParams.length) {
      this.typeVars = new Map(savedTypeVars);
      ctorTypeParams.forEach((p, i) => {
        const explicit = expr.typeArgs[i] ? this.resolveType(expr.typeArgs[i]!) : null;
        this.typeVars.set(p.name, explicit ?? { k: "var", name: p.name, bounds: p.bounds.map((b) => b.name) });
      });
    }
    let argExpected: (Type | undefined)[];
    let argTypes: Type[];
    try {
      argExpected = this.argExpectations(name, local, expr, expected);
      argTypes = expr.args.map((a, i) => this.checkArg(a.value, env, argExpected[i]));
    } finally {
      this.typeVars = savedTypeVars;
    }

    // Calling a first-class function value held in a local variable / parameter,
    // e.g. a param `f: (int) -> int` invoked as `f(x)`. A fn-typed binding takes
    // precedence over any same-named global (it shadows it as a value).
    if (local && local.type.k === "fn") {
      this.recordRef(expr.callee.span, local, local.type);
      this.elab.calls.set(expr, { kind: "fn-value", name });
      return this.checkFnValueCall(name, local.type, expr, argTypes);
    }

    if (this.structs.has(name)) {
      this.elab.calls.set(expr, { kind: "constructor", name });
      const structDecl = this.structs.get(name)!;
      const retType: Type = structDecl.typeParams.length
        ? { k: "struct-generic", name, args: structDecl.typeParams.map(() => UNKNOWN) }
        : { k: "struct", name };
      this.recordRef(expr.callee.span, this.globalDefs.get(name) ?? null, retType);
      const fields = this.structFields.get(name) ?? [];
      if (structDecl.typeParams.length) {
        const args = this.genericConstructor(name, name, structDecl.typeParams, fields, expr, argTypes, env, expected);
        return { k: "struct-generic", name, args };
      }
      if (expr.typeArgs.length > 0)
        this.error(`\`${name}\` is not generic, so it takes no type arguments`, expr.callee.span);
      this.checkConstructorArgs(name, fields, expr, argTypes);
      this.recheckConstructorArgs(expr, env, fields, new Map());
      return { k: "struct", name };
    }
    if (this.variantOf.has(name)) {
      this.elab.calls.set(expr, { kind: "constructor", name });
      const enumName = this.variantOf.get(name)!;
      const enumDecl = this.enums.get(enumName)!;
      const retType: Type = enumDecl.typeParams.length
        ? { k: "enum-generic", name: enumName, args: enumDecl.typeParams.map(() => UNKNOWN) }
        : { k: "enum", name: enumName };
      this.recordRef(expr.callee.span, this.globalDefs.get(name) ?? null, retType);
      const variant = this.variantDecl.get(name)!;
      const fields = this.withTypeVars(enumDecl.typeParams, () =>
        variant.fields.map(
          (f): DefInfo => ({
            name: f.name,
            kind: "field",
            nameSpan: f.nameSpan,
            type: this.resolveType(f.type),
            detail: `${name}.${f.name}: ${typeStr(this.resolveType(f.type))}`,
            container: name,
          })
        )
      );
      if (enumDecl.typeParams.length) {
        // Type arguments on a variant apply to the enum, e.g. `None<int>()` fixes
        // `Option`'s `T`. Explicit args, inference from fields, or (for an
        // argumentless variant like `None()`) the expected type — via the shared
        // helper. The constructed type is the *enum*, so match `expected` on it.
        const args = this.genericConstructor(name, enumName, enumDecl.typeParams, fields, expr, argTypes, env, expected);
        return { k: "enum-generic", name: enumName, args };
      }
      if (expr.typeArgs.length > 0)
        this.error(`\`${name}\` is not generic, so it takes no type arguments`, expr.callee.span);
      this.checkConstructorArgs(name, fields, expr, argTypes);
      this.recheckConstructorArgs(expr, env, fields, new Map());
      return { k: "enum", name: enumName };
    }
    if (ast.isGenericContainer(name)) {
      // A container constructor must name its type — `Set<int>()`, `Dict<K, V>()`.
      // There is no inference from later calls, so a bare `Set()` (whose element
      // type nothing would pin) is an error rather than a lingering `Set<unknown>`.
      const params = containerTypeParams(name);
      if (expr.typeArgs.length !== params.length)
        this.error(
          `\`${name}\` needs its type argument(s) — write \`${name}<${params.join(", ")}>()\``,
          expr.callee.span
        );
      const args = containerArgs(name, (i) =>
        expr.typeArgs[i] ? this.resolveType(expr.typeArgs[i]!) : undefined
      );
      const t: Type = { k: "generic", name, args };
      // Point go-to-definition / hover on the constructed type name (`Dict<…>()`) at
      // its `builtin struct` declaration in the stdlib — as the struct-constructor
      // branch above does for a user struct.
      this.recordRef(expr.callee.span, this.globalDefs.get(name) ?? null, t);
      return t;
    }

    const fn = this.fns.get(name);
    if (fn) {
      this.elab.calls.set(expr, { kind: "fn", name });
      this.noteCall(this.callTargets(`fn:${name}`, name, expr));
      this.recordRef(expr.callee.span, this.globalDefs.get(name) ?? null, this.fnReturn(fn));
      if (fn.typeParams.length > 0) return this.checkGenericCall(fn, expr, argTypes, env);
      if (expr.args.length !== fn.params.length) {
        this.error(`\`${name}\` expects ${fn.params.length} argument(s), got ${expr.args.length}`, expr.span);
      } else {
        fn.params.forEach((p, i) => {
          const want = this.resolveType(p.type);
          const got = argTypes[i]!;
          if (!assignable(want, got, this.implementsTraitType))
            this.error(
              `argument \`${p.name}\` of \`${name}\` expects ${typeStr(want)}, got ${typeStr(got)}`,
              expr.args[i]!.value.span
            );
        });
      }
      return this.fnReturn(fn);
    }

    const builtin = BUILTINS[name];
    if (builtin) {
      this.elab.calls.set(expr, { kind: "builtin", name });
      this.noteCall([{ key: `fn:${name}`, name, span: expr.span }]);
      // Record the callee as a reference to the builtin's stdlib declaration, so
      // hover and go-to-definition resolve it to the prelude source (its
      // `nameSpan` carries the stdlib file `source`).
      this.recordRef(expr.callee.span, {
        name,
        kind: "builtin",
        nameSpan: builtin.nameSpan,
        type: builtin.ret,
        detail: builtin.sig,
      }, builtin.ret);
      // A *generic* builtin (`race<T>(Set<Future<T>>) T`) is typed exactly like a
      // generic user function: infer its type parameters from the arguments and
      // return the substituted result. The flat `BUILTINS.ret` (`unknown` for a type
      // variable) is only right for a non-generic builtin.
      const decl = this.builtinDecls.get(name);
      if (decl && decl.typeParams.length > 0) return this.checkGenericCall(decl, expr, argTypes, env);
      return builtin.ret;
    }

    this.error(`unknown function or type \`${name}\``, expr.span);
    return UNKNOWN;
  }

  /** Check a call through a first-class function value: arity and each argument
   *  against the function type's parameter types; the result is its return type. */
  private checkFnValueCall(
    name: string,
    ft: Extract<Type, { k: "fn" }>,
    expr: ast.CallExpr,
    argTypes: Type[]
  ): Type {
    if (argTypes.length !== ft.params.length) {
      this.error(`\`${name}\` expects ${ft.params.length} argument(s), got ${argTypes.length}`, expr.span);
    } else {
      ft.params.forEach((pt, i) => {
        const got = argTypes[i]!;
        if (!assignable(pt, got, this.implementsTraitType)) {
          this.error(
            `argument ${i + 1} of \`${name}\` expects ${typeStr(pt)}, got ${typeStr(got)}`,
            expr.args[i]!.value.span
          );
        }
      });
    }
    return ft.ret;
  }

  /** Unify each `(declared, arg)` pair to infer a generic callable's type-parameter
   *  bindings — the inference shared by generic function calls and generic
   *  constructors (whose fields play the role of parameters). Reports a conflict when
   *  one parameter is pinned two different ways; `onMismatch(i)` is invoked for each
   *  pair whose shapes don't unify (a function call reports a per-argument type error
   *  there, a constructor defers to `checkConstructorArgs`). Returns the substitution;
   *  finish it with `finalizeTypeArgs`. */
  private unifyArgs(
    pairs: { declared: Type; arg: Type }[],
    conflictWhat: string,
    span: Span,
    onMismatch?: (i: number) => void
  ): Map<string, Type> {
    const subst = new Map<string, Type>();
    const conflicts = new Set<string>();
    pairs.forEach((p, i) => {
      if (!this.unify(p.declared, p.arg, subst, conflicts)) onMismatch?.(i);
    });
    for (const c of conflicts) this.error(`type parameter ${c} in ${conflictWhat}`, span);
    return subst;
  }

  /** Finish a generic instantiation once its arguments are unified: fill any still-
   *  unbound parameter from an `expected` instance of the same constructed type
   *  (bidirectional inference — `x: Option<int> = None()` fixes `T` from the
   *  annotation, not from `None()`'s absent argument), check each inferred argument
   *  against the parameter's bounds, and record the instantiation in the elaboration
   *  so a backend reads it instead of re-inferring. Shared by calls (no `expected`)
   *  and constructors. */
  private finalizeTypeArgs(
    typeParams: ast.TypeParam[],
    subst: Map<string, Type>,
    boundWhat: string,
    expr: ast.CallExpr,
    expected?: Type,
    constructedName?: string
  ): void {
    if (
      expected &&
      constructedName &&
      (expected.k === "struct-generic" || expected.k === "enum-generic") &&
      expected.name === constructedName
    ) {
      typeParams.forEach((p, i) => {
        const cur = subst.get(p.name);
        const fromExpected = expected.args[i];
        if ((!cur || cur.k === "unknown") && fromExpected && fromExpected.k !== "unknown") subst.set(p.name, fromExpected);
      });
    }
    for (const tp of typeParams) {
      const concrete = subst.get(tp.name);
      if (!concrete || concrete.k === "unknown" || concrete.k === "var") continue;
      for (const b of tp.bounds) {
        if (!this.satisfiesBound(concrete, b.name))
          this.error(
            `type \`${typeStr(concrete)}\` does not implement \`${b.name}\` (required by \`${tp.name}\` in ${boundWhat})`,
            expr.span
          );
      }
    }
    this.elab.typeArgs.set(expr, typeParams.map((tp) => subst.get(tp.name) ?? UNKNOWN));
  }

  /**
   * Check a call to a generic function: infer each type parameter from the
   * arguments (unification), verify the inferred type satisfies the parameter's
   * bounds, and return the return type with the parameters substituted away. So
   * `bigger<T: Countable>(a: T, b: T) T` called on two `Set<int>`s type-checks its
   * bounds and returns `Set<int>` — the concrete type is preserved, not erased.
   */
  private checkGenericCall(fn: ast.FnDecl, expr: ast.CallExpr, argTypes: Type[], env: Env): Type {
    // Resolve the declared param/return types with the fn's type variables bound.
    const { paramTypes, retType } = this.withTypeVars(fn.typeParams, () => ({
      paramTypes: fn.params.map((p) => this.resolveTypeQuiet(p.type)),
      retType: fn.returnType ? this.resolveTypeQuiet(fn.returnType) : VOID,
    }));

    if (expr.args.length !== fn.params.length) {
      this.error(`\`${fn.name}\` expects ${fn.params.length} argument(s), got ${expr.args.length}`, expr.span);
      return retType;
    }

    // Infer the type parameters by unifying each parameter type against its arg,
    // then check bounds and record the instantiation (shared with constructors).
    const pairs = fn.params.map((_, i) => ({ declared: paramTypes[i]!, arg: argTypes[i]! }));
    const subst = this.unifyArgs(pairs, `call to \`${fn.name}\``, expr.span, (i) =>
      this.error(
        `argument \`${fn.params[i]!.name}\` of \`${fn.name}\` expects ${typeStr(paramTypes[i]!)}, got ${typeStr(argTypes[i]!)}`,
        expr.args[i]!.value.span
      )
    );
    this.finalizeTypeArgs(fn.typeParams, subst, `\`${fn.name}\``, expr);

    // Second pass: a lambda argument was checked before the type parameters were
    // known, so its parameters (and an under-determined constructor in its body,
    // `() -> None()` in `orElse(() -> a.get(), () -> None())`) recorded a `T =
    // unknown` type. Now that the arguments have pinned them, re-check each lambda
    // against its substituted (concrete) parameter type.
    this.recheckLambdaArgs(expr, env, (i) => this.substitute(paramTypes[i]!, subst));
    return this.substitute(retType, subst);
  }

  /** Re-check any lambda argument against its now-concrete expected type, after a
   *  call's or constructor's type parameters have been resolved. A lambda is first
   *  checked before those are known — so its parameters (and any under-determined
   *  constructor in its body) record `unknown` — and this second pass refines them to
   *  the concrete instantiation, so `elab.types` on the lambda (and its body nodes) is
   *  fully determined for the backends. `expectedAt(i)` gives the i-th argument's
   *  resolved parameter / field type; only a lambda whose expected type is a
   *  fully-concrete function type is redone. Suppressed via `recheckDepth`, so it only
   *  refines recorded types — never re-reports diagnostics or re-counts effect edges. */
  private recheckLambdaArgs(expr: ast.CallExpr, env: Env, expectedAt: (i: number) => Type): void {
    const todo = expr.args
      .map((a, i) => ({ arg: a.value, pt: expectedAt(i) }))
      .filter(({ arg, pt }) => arg.kind === "lambda" && pt.k === "fn" && !hasTypeVar(pt));
    if (!todo.length) return;
    this.recheckDepth++;
    try {
      for (const { arg, pt } of todo) this.checkExpr(arg, env, pt);
    } finally {
      this.recheckDepth--;
    }
  }

  /** Unify a (possibly type-variable-bearing) parameter type against a concrete
   *  argument type, recording each parameter's binding in `subst`. Returns whether
   *  the shapes match; inconsistent bindings for one parameter go in `conflicts`. */
  private unify(param: Type, arg: Type, subst: Map<string, Type>, conflicts: Set<string>): boolean {
    if (param.k === "var") {
      if (arg.k === "unknown") return true;
      const prev = subst.get(param.name);
      if (!prev || prev.k === "unknown") subst.set(param.name, arg);
      else if (!this.sameType(prev, arg)) conflicts.add(`\`${param.name}\` is both ${typeStr(prev)} and ${typeStr(arg)}`);
      return true;
    }
    if (param.k === "unknown" || arg.k === "unknown") return true;
    // Function types unify structurally — so `f: (T) -> U` infers `T`/`U` from
    // the concrete function passed for it.
    if (param.k === "fn" && arg.k === "fn") {
      if (param.params.length !== arg.params.length) return false;
      return (
        param.params.every((p, i) => this.unify(p, arg.params[i]!, subst, conflicts)) &&
        this.unify(param.ret, arg.ret, subst, conflicts)
      );
    }
    if (param.k === "generic" && arg.k === "generic") {
      if (param.name !== arg.name || param.args.length !== arg.args.length) return false;
      return param.args.every((pa, i) => this.unify(pa, arg.args[i]!, subst, conflicts));
    }
    if (param.k === "trait") {
      const actual = arg.k === "trait" && arg.name === param.name
        ? arg.args
        : this.traitArgsFor(arg, param.name);
      if (!actual || param.args.length !== actual.length) return false;
      return param.args.every((pa, i) => this.unify(pa, actual[i]!, subst, conflicts));
    }
    // Generic struct / enum instantiations unify argument-wise, so a parameter
    // `p: Pair<T, int>` infers `T` from the `Pair<..., int>` passed for it.
    if (
      (param.k === "struct-generic" && arg.k === "struct-generic") ||
      (param.k === "enum-generic" && arg.k === "enum-generic")
    ) {
      if (param.name !== arg.name || param.args.length !== arg.args.length) return false;
      return param.args.every((pa, i) => this.unify(pa, arg.args[i]!, subst, conflicts));
    }
    return assignable(param, arg, this.implementsTraitType);
  }

  /** Replace type variables in `t` with their inferred bindings (unbound -> unknown). */
  private substitute(t: Type, subst: Map<string, Type>): Type {
    return this.subst(t, subst, true);
  }

  /** Substitute only the variables present in `subst`, leaving any other variable
   *  in place. Used to specialize a generic type's members by its arguments while
   *  keeping a *method's own* type parameters (which the call site still infers). */
  private substitutePartial(t: Type, subst: Map<string, Type>): Type {
    return this.subst(t, subst, false);
  }

  /** Shared substitution. `eraseUnbound` decides whether a variable absent from
   *  `subst` becomes `unknown` (full inference) or is kept as-is (partial). */
  private subst(t: Type, subst: Map<string, Type>, eraseUnbound: boolean): Type {
    if (t.k === "var") return subst.get(t.name) ?? (eraseUnbound ? UNKNOWN : t);
    if (t.k === "fn")
      return { k: "fn", params: t.params.map((p) => this.subst(p, subst, eraseUnbound)), ret: this.subst(t.ret, subst, eraseUnbound) };
    if (t.k === "generic") return { k: "generic", name: t.name, args: t.args.map((a) => this.subst(a, subst, eraseUnbound)) };
    if (t.k === "enum-generic") return { k: "enum-generic", name: t.name, args: t.args.map((a) => this.subst(a, subst, eraseUnbound)) };
    if (t.k === "struct-generic") return { k: "struct-generic", name: t.name, args: t.args.map((a) => this.subst(a, subst, eraseUnbound)) };
    if (t.k === "trait") return { k: "trait", name: t.name, args: t.args.map((a) => this.subst(a, subst, eraseUnbound)) };
    return t;
  }

  private fnReturn(fn: ast.FnDecl): Type {
    return this.withTypeVars(fn.typeParams, () => (fn.returnType ? this.resolveTypeQuiet(fn.returnType) : VOID));
  }

  /** The first-class function type of a top-level `fn` referenced as a value:
   *  its parameter types and its return type (`void` if none). */
  private fnValueType(fn: ast.FnDecl): Type {
    return {
      k: "fn",
      params: fn.params.map((p) => this.resolveTypeQuiet(p.type)),
      ret: fn.returnType ? this.resolveTypeQuiet(fn.returnType) : VOID,
    };
  }

  /** Resolve a generic struct / enum constructor's type arguments, and check its
   *  field arguments. Type arguments are either **explicit** (`Pair<int, string>
   *  (...)`, `None<int>()`) — validated for arity, with the field arguments then
   *  checked against the substituted field types — or **inferred** by unifying
   *  each field's declared type against its argument (a type parameter fixed to
   *  two different types is an error). Returns the resolved argument list. */
  private genericConstructor(
    name: string,
    constructedName: string,
    typeParams: ast.TypeParam[],
    fields: DefInfo[],
    expr: ast.CallExpr,
    argTypes: Type[],
    env: Env,
    expected?: Type
  ): Type[] {
    // A constructor is inferred just as a generic call is — its fields play the role
    // of parameters. Either the type arguments are given explicitly (`Box<int>(…)`)
    // or each is unified from the field it fills.
    let subst: Map<string, Type>;
    if (expr.typeArgs.length > 0) {
      if (expr.typeArgs.length !== typeParams.length) {
        this.error(
          `\`${name}\` takes ${typeParams.length} type argument(s), but got ${expr.typeArgs.length}`,
          expr.callee.span
        );
      }
      subst = new Map<string, Type>();
      typeParams.forEach((p, i) => subst.set(p.name, expr.typeArgs[i] ? this.resolveType(expr.typeArgs[i]!) : UNKNOWN));
    } else {
      // Unify each field's declared type (bearing type vars) against its argument's
      // type; unlike a call, an argument mismatch is left to `checkConstructorArgs`
      // (which allows partial construction), so no per-argument `onMismatch`.
      const pairs = expr.args.map((arg, i) => ({
        declared: (arg.name ? fields.find((f) => f.name === arg.name) : fields[i])?.type ?? UNKNOWN,
        arg: argTypes[i] ?? UNKNOWN,
      }));
      subst = this.unifyArgs(pairs, `\`${name}\``, expr.span);
    }
    // Check each field argument against its (inferred or explicit) field type.
    // Unbound parameters become unknown, preserving best-effort partial construction.
    const concrete = fields.map((f) => ({ ...f, type: this.substitute(f.type, subst) }));
    this.checkConstructorArgs(name, concrete, expr, argTypes);
    // Fill any parameter the arguments left unbound from the expected instance,
    // bound-check, and record the instantiation — shared with generic calls.
    this.finalizeTypeArgs(typeParams, subst, `\`${name}\``, expr, expected, constructedName);
    this.recheckConstructorArgs(expr, env, fields, subst);
    return typeParams.map((p) => subst.get(p.name) ?? UNKNOWN);
  }

  /** After a constructor's type arguments are known, re-check each argument that
   *  couldn't type itself — a lambda (checked before the parameters it needs), or an
   *  under-determined payload like a bare `None()` — against its field's substituted
   *  type, so the node records that type (`(int) -> int`, `Option<T>`, or a concrete
   *  `Option<int>`) rather than `(unknown) -> int` / `Option<unknown>`. The field type
   *  may still bear the construction's type variables; those resolve at
   *  monomorphization, so — unlike the generic-call lambda re-check — a
   *  type-variable expected is kept, not skipped. Suppressed via `recheckDepth`, so it
   *  only refines recorded types. */
  private recheckConstructorArgs(expr: ast.CallExpr, env: Env, fields: DefInfo[], subst: Map<string, Type>): void {
    const wants = (a: ast.CallArg) => a.value.kind === "lambda" || containsUnknown(this.elab.types.get(a.value));
    if (!expr.args.some(wants)) return;
    this.recheckDepth++;
    try {
      expr.args.forEach((arg, i) => {
        if (!wants(arg)) return;
        const field = arg.name ? fields.find((f) => f.name === arg.name) : fields[i];
        if (field) this.checkExpr(arg.value, env, this.substitute(field.type, subst));
      });
    } finally {
      this.recheckDepth--;
    }
  }

  /** Constructor fields are matched by name/position rather than reordered, but an
   *  omitted field that declares a default is filled here with a fresh clone of that
   *  default (appended as a named argument) so it is checked and lowered per
   *  construction — a `List` default is a distinct list each time — while every
   *  backend, which reads constructor arguments by name, sees it unchanged. A field
   *  omitted *without* a default keeps its zero-value (partial construction). */
  private fillFieldDefaults(expr: ast.CallExpr, fields: ast.FieldDecl[]): void {
    if (!fields.some((f) => f.default)) return;
    const provided = new Set<string>();
    expr.args.forEach((arg, i) => {
      const fname = arg.name ?? fields[i]?.name;
      if (fname) provided.add(fname);
    });
    for (const f of fields) {
      if (f.default && !provided.has(f.name))
        expr.args.push({ name: f.name, nameSpan: f.nameSpan, value: cloneNode(f.default) });
    }
  }

  /** Type-check the arguments passed to a struct / variant constructor. A supplied
   *  arg must name (or positionally line up with) a real field, and no field may be
   *  set twice. Every field must be supplied unless it declares a default — a default
   *  was already materialized as an argument by `fillFieldDefaults`, so any field left
   *  unset here is a required one and is reported missing. */
  private checkConstructorArgs(
    name: string,
    fields: DefInfo[],
    expr: ast.CallExpr,
    argTypes: Type[]
  ): void {
    const seen = new Set<string>();
    expr.args.forEach((arg, i) => {
      const field = arg.name ? fields.find((f) => f.name === arg.name) : fields[i];
      if (arg.name && !field) {
        this.error(`\`${name}\` has no field \`${arg.name}\``, arg.value.span);
        return;
      }
      if (!field) {
        this.error(
          `\`${name}\` has ${fields.length} field(s), but got ${expr.args.length} argument(s)`,
          arg.value.span
        );
        return;
      }
      // A named argument is a use of the field: record it so go-to-definition
      // and hovers jump from `Point(x: 1)` to the `x` field declaration.
      if (arg.name && arg.nameSpan) this.recordRef(arg.nameSpan, field, field.type);
      if (seen.has(field.name)) {
        this.error(`field \`${field.name}\` of \`${name}\` is set more than once`, arg.value.span);
        return;
      }
      seen.add(field.name);
      const got = argTypes[i]!;
      // Skip the assignability check for type-variable fields — their concrete
      // type is inferred by `unify` at the call site, not validated here.
      if (field.type.k !== "var" && !assignable(field.type, got, this.implementsTraitType))
        this.error(
          `field \`${field.name}\` of \`${name}\` expects ${typeStr(field.type)}, got ${typeStr(got)}`,
          arg.value.span
        );
    });
    // A field without a default must be supplied: an omitted required field is a
    // missing argument, not a silent zero value. (Defaulted fields were already
    // filled in by `fillFieldDefaults`, so anything still unset here is required.)
    const missing = fields.filter((f) => !seen.has(f.name)).map((f) => f.name);
    if (missing.length > 0) {
      const list = missing.map((n) => `\`${n}\``).join(", ");
      const noun = missing.length === 1 ? "field" : "fields";
      this.error(`\`${name}\` is missing ${noun} ${list}`, expr.callee.span);
    }
  }

  // -- expectations -------------------------------------------------------

  private expectBool(t: Type, span: Span): void {
    if (t.k !== "bool" && t.k !== "unknown") this.error(`expected bool, got ${typeStr(t)}`, span);
  }
  private expectNumeric(t: Type, span: Span): void {
    if (t.k !== "int" && t.k !== "float" && t.k !== "unknown")
      this.error(`expected a number, got ${typeStr(t)}`, span);
  }
}

/** The inferred result type of a block-bodied lambda from its collected `return`
 *  types: `void` if it never returns a value, the common type when they agree
 *  (ignoring `unknown`), else `unknown` (best-effort when returns disagree). */
function joinReturnTypes(rets: Type[]): Type {
  let joined: Type | null = null;
  for (const t of rets) {
    if (t.k === "unknown") continue;
    if (joined === null) joined = t;
    else if (!sameTypeShallow(joined, t)) return UNKNOWN;
  }
  return joined ?? (rets.length ? UNKNOWN : VOID);
}

/** Exact structural type identity, with `unknown` treated as a wildcard to avoid
 *  cascading diagnostics after an earlier unresolved type. */
function sameTypeExact(a: Type, b: Type): boolean {
  if (a.k === "unknown" || b.k === "unknown") return true;
  if (a.k !== b.k) return false;
  switch (a.k) {
    case "struct":
    case "enum":
    case "var":
      return a.name === (b as { name: string }).name;
    case "trait": {
      const bt = b as Extract<Type, { k: "trait" }>;
      return (
        a.name === bt.name &&
        a.args.length === bt.args.length &&
        a.args.every((x, i) => sameTypeExact(x, bt.args[i]!))
      );
    }
    case "fn": {
      const bf = b as Extract<Type, { k: "fn" }>;
      return (
        a.params.length === bf.params.length &&
        a.params.every((p, i) => sameTypeExact(p, bf.params[i]!)) &&
        sameTypeExact(a.ret, bf.ret)
      );
    }
    case "generic":
    case "struct-generic":
    case "enum-generic": {
      const bg = b as Extract<Type, { k: "generic" | "struct-generic" | "enum-generic" }>;
      return (
        a.name === bg.name &&
        a.args.length === bg.args.length &&
        a.args.every((x, i) => sameTypeExact(x, bg.args[i]!))
      );
    }
    default:
      return true;
  }
}

/** Structural type identity for joining lambda return types. Mirrors the
 *  checker's `sameType` but as a free function (no trait/`self` context needed). */
function sameTypeShallow(a: Type, b: Type): boolean {
  if (a.k !== b.k) return false;
  switch (a.k) {
    case "struct":
    case "enum":
    case "var":
      return a.name === (b as { name: string }).name;
    case "trait": {
      const bt = b as Extract<Type, { k: "trait" }>;
      return (
        a.name === bt.name &&
        a.args.length === bt.args.length &&
        a.args.every((x, i) => sameTypeShallow(x, bt.args[i]!))
      );
    }
    case "fn": {
      const bf = b as Extract<Type, { k: "fn" }>;
      return (
        a.params.length === bf.params.length &&
        a.params.every((p, i) => sameTypeShallow(p, bf.params[i]!)) &&
        sameTypeShallow(a.ret, bf.ret)
      );
    }
    case "generic":
    case "struct-generic":
    case "enum-generic": {
      const bg = b as Extract<Type, { k: "generic" | "struct-generic" | "enum-generic" }>;
      return (
        a.name === bg.name &&
        a.args.length === bg.args.length &&
        a.args.every((x, i) => sameTypeShallow(x, bg.args[i]!))
      );
    }
    default:
      return true; // same primitive kind
  }
}

/** Render a source-level type expression for diagnostics (before it's resolved
 *  to a `Type`). */
function typeExprStr(te: ast.TypeExpr): string {
  if (te.kind === "fn") return `(${te.params.map(typeExprStr).join(", ")}) -> ${typeExprStr(te.ret)}`;
  return te.args.length ? `${te.name}<${te.args.map(typeExprStr).join(", ")}>` : te.name;
}

/** The span of just the leading identifier within a (possibly wider) span. */
/** Render an effect-system callable key for a diagnostic: `fn:print` -> `print`,
 *  `method:Circle.show` -> `Circle.show`. Keeps the qualifier on a method so a
 *  call path stays unambiguous when two types share a method name. */
function renderCallable(key: string): string {
  if (key.startsWith("fn:")) return key.slice(3);
  if (key.startsWith("method:")) return key.slice(7);
  return key;
}

/** Does a span point at real source (rather than the zero-width offset-0
 *  sentinel some synthetic definitions carry)? Only located definitions get a
 *  usable `DefId`. */
function located(span: Span): boolean {
  return span.end.offset > span.start.offset || span.start.offset > 0;
}

function nameSpan(span: Span, name: string): Span {
  return {
    start: span.start,
    end: {
      offset: span.start.offset + name.length,
      line: span.start.line,
      col: span.start.col + name.length,
    },
    // Keep the source: a prelude type reference's use span is rebuilt here from
    // the neutralized offset-0 sentinel, and must stay tagged as stdlib so it is
    // never read as a position in the user's buffer.
    source: span.source,
  };
}

function fnSignature(fn: ast.FnDecl, ts: (t: ast.TypeExpr) => string): string {
  const tp = fn.typeParams.length
    ? `<${fn.typeParams
        .map((p) => (p.bounds.length ? `${p.name}: ${p.bounds.map((b) => b.name).join(" + ")}` : p.name))
        .join(", ")}>`
    : "";
  const params = fn.params.map((p) => `${p.name}: ${ts(p.type)}`).join(", ");
  const ret = fn.returnType ? ` ${ts(fn.returnType)}` : "";
  return `fn ${fn.name}${tp}(${params})${ret}`;
}

function variantSignature(
  enumName: string,
  v: ast.EnumVariant,
  ts: (t: ast.TypeExpr) => string
): string {
  if (v.fields.length === 0) return `${enumName}.${v.name}`;
  const fields = v.fields.map((f) => `${f.name}: ${ts(f.type)}`).join(", ");
  return `${enumName}.${v.name}(${fields})`;
}

function numeric(t: Type): boolean {
  return t.k === "int" || t.k === "float";
}

/** Does `t` mention a type variable? A type still carrying one is only partly
 *  resolved, so it isn't pushed into a lambda parameter as an expected type
 *  (see `checkLambda`). */
function hasTypeVar(t: Type): boolean {
  switch (t.k) {
    case "var":
      return true;
    case "fn":
      return t.params.some(hasTypeVar) || hasTypeVar(t.ret);
    case "generic":
    case "enum-generic":
    case "struct-generic":
    case "trait":
      return t.args.some(hasTypeVar);
    default:
      return false;
  }
}

/** Whether a type is under-determined — absent, `unknown`, or containing `unknown`
 *  anywhere in its arguments. Used to decide which constructor arguments need a
 *  re-check once the construction's type arguments are known (`recheckConstructorArgs`). */
function containsUnknown(t: Type | undefined): boolean {
  if (!t) return true;
  switch (t.k) {
    case "unknown":
      return true;
    case "fn":
      return containsUnknown(t.ret) || t.params.some(containsUnknown);
    case "generic":
    case "enum-generic":
    case "struct-generic":
    case "trait":
      return t.args.some(containsUnknown);
    default:
      return false;
  }
}

/** May `l` and `r` be compared with `==` / `!=`? Equality is not assignment —
 *  neither side is a slot the other flows into — so a pair compares when *either*
 *  direction is assignable. That keeps the compatible cases (`int` against
 *  `float`, a concrete type against a trait it implements, an `Option<int>`
 *  against a partly-inferred `Option<unknown>`) while rejecting a pair that can
 *  never be equal (`Option<int>` against `int`). `unknown` stays a wildcard, so an
 *  earlier error doesn't cascade into a second one here. */
function comparable(
  l: Type,
  r: Type,
  implementsTrait?: (value: Type, trait: Extract<Type, { k: "trait" }>) => boolean
): boolean {
  // A type variable in a generic body has no concrete identity yet; its
  // instantiations are checked at the call site, so comparing one stays lenient.
  if (l.k === "var" || r.k === "var") return true;
  return assignable(l, r, implementsTrait) || assignable(r, l, implementsTrait);
}

function assignable(
  target: Type,
  value: Type,
  implementsTrait?: (value: Type, trait: Extract<Type, { k: "trait" }>) => boolean
): boolean {
  if (target.k === "unknown" || value.k === "unknown") return true;
  // Type variables (inside a generic fn body): a `var` slot accepts only the same
  // variable; a `var` value flows into a trait slot when the trait is among its
  // bounds. Concrete inference at call sites is handled by `unify`, not here.
  if (target.k === "var") return value.k === "var" && value.name === target.name;
  if (value.k === "var") return target.k === "trait" && value.bounds.includes(target.name);
  // A trait-typed slot accepts any concrete type that implements the trait (or
  // the same trait). Without impl info the check stays lenient.
  if (target.k === "trait") {
    if (value.k === "trait")
      return (
        value.name === target.name &&
        value.args.length === target.args.length &&
        target.args.every((arg, i) => sameTypeExact(arg, value.args[i]!))
      );
    // A concrete type (struct/enum) or a generic container implements the trait;
    // the container is matched by its head name (`Set`).
    if (
      value.k === "struct" ||
      value.k === "struct-generic" ||
      value.k === "enum" ||
      value.k === "enum-generic" ||
      value.k === "generic"
    )
      return !implementsTrait || implementsTrait(value, target);
    return false;
  }
  if (numeric(target) && numeric(value)) return true;
  // Function types: same arity, each parameter and the return assignable. (Kept
  // lenient — like the rest of `assignable` — rather than strictly contravariant;
  // a named-function reference is an exact match anyway.)
  if (target.k === "fn" && value.k === "fn")
    return (
      target.params.length === value.params.length &&
      target.params.every((p, i) => assignable(p, value.params[i]!, implementsTrait)) &&
      assignable(target.ret, value.ret, implementsTrait)
    );
  if (target.k === "generic" && value.k === "generic")
    return (
      target.name === value.name &&
      target.args.length === value.args.length &&
      target.args.every((a, i) => assignable(a, value.args[i]!, implementsTrait))
    );
  if (target.k === "struct" && value.k === "struct") return target.name === value.name;
  if (target.k === "enum" && value.k === "enum") return target.name === value.name;
  if (
    (target.k === "enum-generic" && value.k === "enum-generic") ||
    (target.k === "struct-generic" && value.k === "struct-generic")
  )
    return (
      target.name === value.name &&
      target.args.length === value.args.length &&
      target.args.every((a, i) => assignable(a, value.args[i]!, implementsTrait))
    );
  return target.k === value.k;
}

/** The primitive `Type` a type *name* denotes (`str`/`char`/`int`/…), or null if
 *  the name isn't a primitive. `str` is the accepted alias for `string`. */
function primitiveType(name: string): Type | null {
  switch (name) {
    case "int": return INT;
    case "float": return FLOAT;
    case "bool": return BOOL;
    case "string": case "str": return STRING;
    case "char": return CHAR;
    default: return null;
  }
}

export function typeStr(t: Type): string {
  switch (t.k) {
    case "generic":
    case "enum-generic":
    case "struct-generic":
    case "trait":
      return t.args.length ? `${t.name}<${t.args.map(typeStr).join(", ")}>` : t.name;
    case "fn":
      return `(${t.params.map(typeStr).join(", ")}) -> ${typeStr(t.ret)}`;
    case "struct":
    case "enum":
    case "var":
      return t.name;
    default:
      return t.k;
  }
}
